import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Layers, Users, CheckSquare, Plus, Trash2, ChevronDown, ChevronRight, Pencil,
  ExternalLink, AlertCircle, Brain, Link2, Globe, Info, Sparkles, Loader2, Check, X,
  Building2, Shield, GitBranch,
} from 'lucide-react'
import { useScopeStore } from '../store'
import { showToast } from '@store/uiStore'
import { getDefaultDataInputs } from '../utils/defaults'
import { getIdTypesForCountry } from '@features/dashboards/components/MatchRateTool/data/idTypes'
import { COUNTRIES } from '@features/dashboards/components/PocTool/data/countries'
import { scopeVersionsApi, type DealTranscript, type HubspotExcerpt } from '@api/client'
import { TranscriptSourcePanel } from './TranscriptSourcePanel'
import { useCascadeStore } from '@features/dashboards/components/CascadeBuilder/store'
import type { CascadeNode, CascadeEdge } from '@features/dashboards/components/CascadeBuilder/types'
import { CascadePreview } from './CascadePreview'
import { cascadeApi } from '@api/client'
import { fetchBlueprintContext } from '@features/dashboards/components/CascadeBuilder/index'
import type { BlueprintConfig, BlueprintRuleset, BlueprintFuzziness, BlueprintStakeholder } from '../types'




const ENGAGEMENT_STATES: { value: BlueprintStakeholder['engagementState']; label: string }[] = [
  { value: 'evaluator', label: 'Technical Evaluator' },
  { value: 'champion_candidate', label: 'Champion Candidate' },
  { value: 'champion', label: 'Champion' },
  { value: 'blocker', label: 'Blocker' },
  { value: 'unknown', label: 'Unknown' },
]

const CAPTURE_METHODS = [
  { value: 'ocr', label: 'OCR from document' },
  { value: 'user_vendor_form', label: 'User input (vendor form)' },
  { value: 'user_client_form', label: 'User input (client form)' },
  { value: 'existing', label: 'Existing record' },
  { value: 'lookup', label: 'Lookup / enrichment' },
  { value: 'other', label: 'Other (see notes)' },
]

const ADDRESS_FORMATS = [
  { value: 'single_line', label: 'Single line / free text' },
  { value: 'line1_line2', label: 'Address line 1 + line 2' },
  { value: 'structured', label: 'Structured (street, apt, city, etc.)' },
  { value: 'google_places', label: 'Google Places API' },
  { value: 'other_autocomplete', label: 'Other autocomplete' },
]

const DOC_SCAN_VENDORS = ['Onfido', 'Jumio', 'IDnow', 'Incode', 'Persona', 'Veriff', 'Microblink', 'In-house']
const SELFIE_VENDORS = ['Onfido', 'Jumio', 'IDnow', 'Incode', 'Persona', 'Veriff', 'iProov', 'In-house']
const DEVICE_DATA_VENDORS = ['Sardine', 'Socure', 'ThreatMetrix', 'Telesign', 'Sift', 'Castle', 'In-house']

const RULESET_OPTIONS: { value: BlueprintRuleset; label: string }[] = [
  { value: '1+1', label: '1+1' },
  { value: '2+2', label: '2+2' },
  { value: 'custom', label: 'Custom' },
  { value: 'unknown', label: 'Unknown' },
]


// --- Collapsible section wrapper ---
function Section({ title, subtitle, icon, children, defaultOpen = true, badge, accentColor = 'blue', complete, headerAction }: {
  title: string
  subtitle: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
  badge?: React.ReactNode
  accentColor?: 'blue' | 'violet' | 'emerald' | 'amber' | 'cyan' | 'rose' | 'orange' | 'slate'
  complete?: boolean
  headerAction?: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  const borderColorMap = {
    blue: 'border-blue-500/30', violet: 'border-violet-500/30', emerald: 'border-emerald-500/30',
    amber: 'border-amber-500/30', cyan: 'border-cyan-500/30', rose: 'border-rose-500/30',
    orange: 'border-orange-500/30', slate: 'border-slate-500/30',
  }
  const headerBgMap = {
    blue: 'bg-blue-500/5', violet: 'bg-violet-500/5', emerald: 'bg-emerald-500/5',
    amber: 'bg-amber-500/5', cyan: 'bg-cyan-500/5', rose: 'bg-rose-500/5',
    orange: 'bg-orange-500/5', slate: 'bg-slate-500/5',
  }
  const iconColorMap = {
    blue: 'text-blue-400', violet: 'text-violet-400', emerald: 'text-emerald-400',
    amber: 'text-amber-400', cyan: 'text-cyan-400', rose: 'text-rose-400',
    orange: 'text-orange-400', slate: 'text-slate-400',
  }
  const stripMap = {
    blue: 'bg-blue-500', violet: 'bg-violet-500', emerald: 'bg-emerald-500',
    amber: 'bg-amber-500', cyan: 'bg-cyan-500', rose: 'bg-rose-500',
    orange: 'bg-orange-500', slate: 'bg-slate-500',
  }

  return (
    <div className={`border rounded-lg bg-[var(--bg-primary)] overflow-hidden ${borderColorMap[accentColor]}`}>
      <div className={`flex items-center gap-3 px-4 py-3 ${headerBgMap[accentColor]}`}>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-3 flex-1 min-w-0 hover:brightness-110 transition-all text-left"
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${headerBgMap[accentColor]} ${iconColorMap[accentColor]}`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`font-semibold text-sm ${iconColorMap[accentColor]}`}>{title}</span>
              {complete !== undefined && (
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${complete ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'}`} title={complete ? 'Complete' : 'Incomplete'} />
              )}
              {badge}
            </div>
            <p className="text-xs text-[var(--text-muted)] truncate">{subtitle}</p>
          </div>
          {open ? <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--text-muted)]" />}
        </button>
        {headerAction && <div onClick={(e) => e.stopPropagation()}>{headerAction}</div>}
      </div>
      {open && (
        <div className="relative">
          <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${stripMap[accentColor]} opacity-40`} />
          <div className="px-4 pb-4 border-t border-[var(--border-color)]">{children}</div>
        </div>
      )}
    </div>
  )
}


// --- Discovery Questions (collapsible accordion inside As-Is Architecture) ---
const DISCOVERY_QUESTIONS: { field: 'jurisdictionsInScope' | 'onboardingJourney' | 'checksBeforeTransact' | 'riskSegmentation' | 'eddTriggers' | 'sanctionsPepScreening' | 'ongoingMonitoring' | 'frictionFalsePositives'; question: string; placeholder: string }[] = [
  { field: 'jurisdictionsInScope', question: 'Which countries or regions are in scope today, and are there plans to expand?', placeholder: 'e.g. Australia, New Zealand, Philippines — expanding to Singapore and Indonesia in Q3' },
  { field: 'onboardingJourney', question: 'Walk us through how a new customer or user gets onboarded end-to-end today.', placeholder: 'e.g. User signs up via mobile app → email verification → document upload → manual back-office review (24-48h) → account activated' },
  { field: 'checksBeforeTransact', question: 'What identity, verification, or compliance checks need to pass before someone can transact or access services?', placeholder: 'e.g. ID verification + liveness check must pass, AML screening clear, address verified before fiat on-ramp is enabled' },
  { field: 'riskSegmentation', question: 'How do you differentiate between low-, medium-, and high-risk customers or entities today?', placeholder: 'e.g. Risk tier based on country + product type + transaction volume — high-risk triggers additional document collection and ongoing monitoring' },
  { field: 'eddTriggers', question: 'What conditions trigger enhanced due diligence, step-up verification, or manual review?', placeholder: 'e.g. PEP match, high-risk jurisdiction, transaction above $10K, IDV confidence score below threshold, adverse media hit' },
  { field: 'sanctionsPepScreening', question: 'How are you currently screening for sanctions, PEPs, and adverse media — and at what points in the lifecycle?', placeholder: 'e.g. Refinitiv WorldCheck at onboarding only for sanctions + PEP, no adverse media screening, no ongoing rescreening' },
  { field: 'ongoingMonitoring', question: 'After onboarding, how are you monitoring customers for ongoing risk — re-verification, transaction monitoring, or continuous screening?', placeholder: 'e.g. Annual re-screening batch, no continuous monitoring, manual review of flagged transactions, considering real-time AML monitoring' },
  { field: 'frictionFalsePositives', question: 'Where are you seeing the most friction, drop-off, false positives, or operational bottlenecks in your current process?', placeholder: 'e.g. 40% drop-off at document upload, high false positive rate on AML screening, manual review queue averaging 3-day backlog' },
]

function DiscoveryQuestions({ bp, updateBlueprintField, highlightedFields }: { bp: BlueprintConfig; updateBlueprintField: (field: typeof DISCOVERY_QUESTIONS[number]['field'], value: string) => void; highlightedFields?: Set<string> }) {
  const [open, setOpen] = useState(false)
  const [expandedQ, setExpandedQ] = useState<Set<string>>(new Set())
  const [flashFields, setFlashFields] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState(false)
  const answered = DISCOVERY_QUESTIONS.filter((q) => bp[q.field]?.trim()).length

  // Auto-open accordion and expand questions when AI applies a discovery field
  useEffect(() => {
    if (highlightedFields && highlightedFields.size > 0) {
      setOpen(true)
      setExpandedQ((prev) => new Set([...prev, ...highlightedFields]))
      setFlashFields(new Set(highlightedFields))
      const timer = setTimeout(() => setFlashFields(new Set()), 2000)
      return () => clearTimeout(timer)
    }
  }, [highlightedFields])

  const toggleQ = (field: string) => {
    setExpandedQ((prev) => {
      const next = new Set(prev)
      next.has(field) ? next.delete(field) : next.add(field)
      return next
    })
  }

  return (
    <div className={`border rounded-lg overflow-hidden ${answered === DISCOVERY_QUESTIONS.length ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-blue-500/30 bg-blue-500/5'}`}>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors text-left ${answered === DISCOVERY_QUESTIONS.length ? 'hover:bg-emerald-500/10' : 'hover:bg-blue-500/10'}`}
      >
        {open ? <ChevronDown className={`w-4 h-4 ${answered === DISCOVERY_QUESTIONS.length ? 'text-emerald-400' : 'text-blue-400'}`} /> : <ChevronRight className={`w-4 h-4 ${answered === DISCOVERY_QUESTIONS.length ? 'text-emerald-400' : 'text-blue-400'}`} />}
        <span className={`text-xs font-semibold uppercase tracking-wide flex-1 ${answered === DISCOVERY_QUESTIONS.length ? 'text-emerald-400' : 'text-blue-400'}`}>Discovery Questions</span>
        {open && answered > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(!editing) }}
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-md border transition-colors ${
              editing
                ? 'text-blue-400 bg-blue-500/15 border-blue-500/30'
                : 'text-[var(--text-muted)] bg-[var(--bg-primary)] border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]'
            }`}
          >
            <Pencil className="w-2.5 h-2.5" /> {editing ? 'Done' : 'Edit'}
          </button>
        )}
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${answered === DISCOVERY_QUESTIONS.length ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-300'}`}>
          {answered}/{DISCOVERY_QUESTIONS.length}
        </span>
      </button>
      {open && (
        <div className={`border-t ${answered === DISCOVERY_QUESTIONS.length ? 'border-emerald-500/20' : 'border-blue-500/20'}`}>
          {DISCOVERY_QUESTIONS.map((q) => {
            const isExpanded = expandedQ.has(q.field)
            const hasValue = bp[q.field]?.trim()
            return (
              <div key={q.field} className={`border-b last:border-b-0 transition-colors duration-700 ${flashFields.has(q.field) ? 'bg-emerald-500/15' : ''} ${answered === DISCOVERY_QUESTIONS.length ? 'border-emerald-500/10' : 'border-blue-500/10'}`}>
                <button
                  onClick={() => toggleQ(q.field)}
                  className={`w-full flex items-center gap-2 px-3 py-2 transition-colors text-left ${answered === DISCOVERY_QUESTIONS.length ? 'hover:bg-emerald-500/10' : 'hover:bg-blue-500/10'}`}
                >
                  {isExpanded ? <ChevronDown className="w-3 h-3 text-blue-400 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-blue-400 flex-shrink-0" />}
                  <span className="text-xs text-[var(--text-primary)] flex-1">{q.question}</span>
                  {hasValue ? <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" /> : <span className="w-1.5 h-1.5 rounded-full bg-blue-400/50 flex-shrink-0 animate-pulse" />}
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3 pt-0.5 pl-8">
                    {editing || !hasValue ? (
                    <textarea
                      className="w-full px-3 py-1.5 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y"
                      rows={3}
                      placeholder={q.placeholder}
                      value={bp[q.field]}
                      onChange={(e) => updateBlueprintField(q.field, e.target.value)}
                    />
                    ) : (
                    <p className="text-xs text-[var(--text-primary)] whitespace-pre-wrap">{bp[q.field]}</p>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// --- Multi-select dropdown (compact, for vendors & IDs) ---
function MultiSelectDropdown({ options, selected, onChange, placeholder }: {
  options: string[]
  selected: string[]
  onChange: (v: string[]) => void
  placeholder: string
}) {
  const label = selected.length === 0 ? placeholder
    : selected.length === 1 ? selected[0]
    : `${selected.length} selected`
  return (
    <select value="" onChange={(e) => {
      if (!e.target.value) return
      const val = e.target.value
      onChange(selected.includes(val) ? selected.filter((s) => s !== val) : [...selected, val])
      e.target.value = ''
    }} className="px-1.5 py-0 text-[10px] rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] cursor-pointer min-w-[7rem]">
      <option value="">{label}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{selected.includes(opt) ? '✓ ' : '  '}{opt}</option>
      ))}
    </select>
  )
}

// --- Collapsible instructions for Country Rulesets ---
function InstructionsPanel() {
  const [open, setOpen] = useState(false)
  return (
    <div className="mb-3">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400 hover:text-emerald-300 font-medium transition-colors">
        <Info className="w-3.5 h-3.5" />
        {open ? 'Hide instructions' : 'How to complete this section'}
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-2 p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 text-xs text-[var(--text-secondary)] space-y-2">
          <p className="font-semibold text-emerald-400">For each country the customer operates in:</p>
          <ol className="list-decimal list-inside space-y-1.5 pl-1">
            <li><strong>Identify the country</strong> — select the country the customer currently verifies in or plans to verify in.</li>
            <li><strong>Set the ruleset</strong> — select the matching ruleset they use (1+1, 2+2, or custom). If unknown, leave it as Unknown (orange) and confirm with the customer.</li>
            <li><strong>Document data inputs</strong> — toggle on the data inputs they collect (name, DOB, address, etc.) and set fuzziness per field. Fill in the capture method and any notes for each.</li>
            <li><strong>Record current pass rate</strong> — what pass rate are they achieving today? This is critical for understanding their baseline.</li>
            <li><strong>Set a target pass rate</strong> — what pass rate do they need to achieve? This drives the solution design.</li>
            <li><strong>Confirm details</strong> — click the <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-amber-500 border border-amber-400 text-white text-[7px] font-bold align-middle mx-0.5">?</span> icons to turn them into <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-500 border border-emerald-400 text-white text-[7px] font-bold align-middle mx-0.5">✓</span> once a detail has been confirmed by the customer. This tracks what we know vs what we still need to verify.</li>
          </ol>
          <p className="text-[var(--text-muted)] italic">At least one country must be fully documented and confirmed for this section to be marked complete.</p>
        </div>
      )}
    </div>
  )
}

// --- Searchable country selector ---
function CountrySearch({ value, onChange, usedCodes = [] }: { value: string; onChange: (code: string) => void; usedCodes?: string[] }) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const selected = COUNTRIES.find((c) => c.code === value)
  const filtered = search.trim()
    ? COUNTRIES.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.code.toLowerCase().includes(search.toLowerCase()))
    : COUNTRIES

  return (
    <div className="relative w-52">
      <input
        type="text"
        placeholder={selected ? `${selected.name} (${selected.code})` : 'Search country...'}
        value={open ? search : ''}
        onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className={`w-full px-2 py-1.5 text-sm font-semibold rounded-lg border-2 border-emerald-500/30 bg-emerald-500/10 text-emerald-400 placeholder:text-emerald-400/60 outline-none ${!selected && !open ? 'placeholder:text-[var(--text-muted)]' : ''}`}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-lg">
          {filtered.slice(0, 50).map((c) => {
            const isUsed = c.code !== value && usedCodes.includes(c.code)
            return (
            <button
              key={c.code}
              disabled={isUsed}
              onMouseDown={(e) => { e.preventDefault(); if (!isUsed) { onChange(c.code); setSearch(''); setOpen(false) } }}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${isUsed ? 'text-[var(--text-muted)] opacity-40 cursor-not-allowed' : c.code === value ? 'bg-emerald-500/15 text-emerald-400 font-semibold' : 'text-[var(--text-primary)] hover:bg-emerald-500/10'}`}
            >
              {c.name} <span className="text-[var(--text-muted)]">({c.code})</span>{isUsed && <span className="ml-1 text-[9px]">(already added)</span>}
            </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// --- Per-country completion calculation ---
function getCountryCompletion(cr: { country: string; ruleset: string; dataInputs: { enabled: boolean; confirmed: boolean; captureMethod: string }[]; passRate: string; passRateConfirmed: boolean; desiredPassRate: string; desiredPassRateConfirmed: boolean; notes: string }): number {
  const checks = [
    cr.country !== '',                             // Country selected
    cr.ruleset !== 'unknown',                      // Ruleset set
    cr.dataInputs.some((di) => di.enabled),        // At least one data input enabled
    cr.dataInputs.filter((di) => di.enabled).every((di) => di.confirmed), // All enabled inputs confirmed
    cr.dataInputs.filter((di) => di.enabled).every((di) => di.captureMethod !== ''), // All enabled inputs have capture method
    cr.passRate !== '',                             // Current pass rate documented
    cr.passRateConfirmed,                          // Current pass rate confirmed
    cr.desiredPassRate !== '',                      // Target pass rate documented
    cr.desiredPassRateConfirmed,                   // Target pass rate confirmed
  ]
  const applicable = cr.dataInputs.some((di) => di.enabled) ? checks : checks.slice(0, 3) // If no inputs enabled, only first 3 matter
  return Math.round((applicable.filter(Boolean).length / applicable.length) * 100)
}

// --- AI Suggestions Modal ---
interface AiStakeholderSuggestion {
  name: string
  role: string
  technicalConcerns: string
  engagementState: BlueprintStakeholder['engagementState']
}

interface AiDataInputSuggestion {
  type: string
  enabled: boolean
  fuzziness?: string
  captureMethod?: string
  notes?: string
}

interface AiCountrySuggestion {
  country: string
  ruleset: string
  notes: string
  docScanVendors?: string[]
  selfieVendors?: string[]
  deviceDataVendors?: string[]
  nationalIds?: string[]
  passRate?: string
  desiredPassRate?: string
  dataInputs?: AiDataInputSuggestion[]
}

type AiStrength = 'strong' | 'moderate' | 'weak'

interface AiTextField {
  text: string
  summary?: string
  strength: AiStrength
}

interface AiDiscoveryQuestions {
  jurisdictionsInScope?: AiTextField | string | null
  onboardingJourney?: AiTextField | string | null
  checksBeforeTransact?: AiTextField | string | null
  riskSegmentation?: AiTextField | string | null
  eddTriggers?: AiTextField | string | null
  sanctionsPepScreening?: AiTextField | string | null
  ongoingMonitoring?: AiTextField | string | null
  frictionFalsePositives?: AiTextField | string | null
}

interface AiVerificationStackSuggestion {
  layer: string
  vendor: string
  owner: string
  painPoints: string
}

interface AiMaturitySuggestion {
  id?: string // predefined dimension ID (e.g. "verification_architecture")
  dimension: string
  score: number // legacy: AI returns 1-5
  stage?: number // new: AI returns 0-3 directly
  notes: string
  evidence?: string // new: maps to evidence field
}

interface AiPriorityGapSuggestion {
  rank: number
  description: string
  impact: string
}

interface AiRecommendedActionSuggestion {
  text: string
}

interface AiSuggestion {
  businessModel?: AiTextField | string | null
  monthlyOnboardingVolume?: AiTextField | string | null
  strategicPriorities?: AiTextField | string | null
  stpRate?: AiTextField | string | null
  fraudRate?: AiTextField | string | null
  problemHypothesis?: AiTextField | string | null
  surfacePain?: AiTextField | string | null
  deepPain?: AiTextField | string | null
  whyNow?: AiTextField | string | null
  regulatoryContext?: AiTextField | string | null
  regulatoryIntel?: AiTextField | string | null
  currentProcess?: AiTextField | string | null
  stakeholders?: AiStakeholderSuggestion[] | null
  countryRulesets?: AiCountrySuggestion[] | null
  discoveryQuestions?: AiDiscoveryQuestions | null
  verificationStack?: AiVerificationStackSuggestion[] | null
  maturityDimensions?: AiMaturitySuggestion[] | null
  priorityGaps?: AiPriorityGapSuggestion[] | null
  quickWins?: AiRecommendedActionSuggestion[] | null
  foundationMoves?: AiRecommendedActionSuggestion[] | null
  strategicShifts?: AiRecommendedActionSuggestion[] | null
}

/** Normalize a field that may be a string (old format) or {text, summary?, strength} object */
function parseAiField(val: AiTextField | string | null | undefined): { text: string; summary?: string; strength: AiStrength } | null {
  if (!val) return null
  if (typeof val === 'string') return val.trim() ? { text: val, strength: 'moderate' } : null
  if (val.text?.trim()) return { text: val.text, summary: val.summary?.trim() || undefined, strength: val.strength || 'moderate' }
  return null
}

const STRENGTH_CONFIG: Record<AiStrength, { label: string; color: string; bg: string; border: string; actionable: boolean }> = {
  strong: { label: 'Strong evidence', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/25', actionable: true },
  moderate: { label: 'More info required', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/25', actionable: false },
  weak: { label: 'More info required', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/25', actionable: false },
}

type BlueprintApplyField = 'businessModel' | 'monthlyOnboardingVolume' | 'strategicPriorities' | 'strategicPrioritiesSummary' | 'stpRate' | 'fraudRate' | 'problemHypothesis' | 'problemHypothesisSummary' | 'surfacePain' | 'surfacePainSummary' | 'deepPain' | 'deepPainSummary' | 'whyNow' | 'whyNowSummary' | 'regulatoryContext' | 'regulatoryContextSummary' | 'regulatoryIntel' | 'currentProcess' | 'currentProcessSummary' | keyof AiDiscoveryQuestions

const SUMMARY_FIELD_MAP: Partial<Record<BlueprintApplyField, BlueprintApplyField>> = {
  strategicPriorities: 'strategicPrioritiesSummary',
  problemHypothesis: 'problemHypothesisSummary',
  surfacePain: 'surfacePainSummary',
  deepPain: 'deepPainSummary',
  whyNow: 'whyNowSummary',
  regulatoryContext: 'regulatoryContextSummary',
  currentProcess: 'currentProcessSummary',
}

function CurrentValueDrawer({ value, label }: { value: string; label: string }) {
  const [open, setOpen] = useState(false)
  if (!value?.trim()) return null
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-[10px] font-medium text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
      >
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`} />
        Currently in {label}
      </button>
      {open && (
        <div className="mt-1.5 px-3 py-2 rounded border border-[var(--border-color)] bg-[var(--bg-primary)]">
          <p className="text-xs text-[var(--text-muted)] whitespace-pre-wrap">{value}</p>
        </div>
      )}
    </div>
  )
}

function RegulatoryIntelDrawer({ intel }: { intel: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-[10px] font-medium text-rose-400 hover:text-rose-300 transition-colors"
      >
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`} />
        AI Intel (SE only — not visible to customer)
      </button>
      {open && (
        <div className="mt-1.5 px-3 py-2 rounded border border-rose-500/20 bg-rose-500/5">
          <p className="text-xs text-[var(--text-muted)] whitespace-pre-wrap">{intel}</p>
        </div>
      )}
    </div>
  )
}

function BlueprintSuggestModal({ dealId, onApply, onAddStakeholder, onAddCountry, onReplaceVerificationStack, currentBlueprint }: {
  dealId: number | null
  onApply: (field: BlueprintApplyField, value: string) => void
  onAddStakeholder: (s: AiStakeholderSuggestion) => void
  onAddCountry: (c: AiCountrySuggestion) => void
  onReplaceVerificationStack: (layers: AiVerificationStackSuggestion[]) => void
  currentBlueprint: BlueprintConfig
}) {
  const [open, setOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const [includeHubspot, setIncludeHubspot] = useState(true)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ suggestions: AiSuggestion; confidence: string; reasoning: string; warnings: string[] } | null>(null)
  const [applied, setApplied] = useState<Set<string>>(new Set())
  const [selectedTranscriptIds, setSelectedTranscriptIds] = useState<Set<number>>(new Set())
  const [savedTranscripts, setSavedTranscripts] = useState<DealTranscript[]>([])
  const [hubspotExcerpt, setHubspotExcerpt] = useState<HubspotExcerpt | null>(null)
  const [excerptLoading, setExcerptLoading] = useState(false)
  const [includeExcerpt, setIncludeExcerpt] = useState(true)

  const handleTranscriptsLoaded = (transcripts: DealTranscript[]) => {
    setSavedTranscripts(transcripts)
    setSelectedTranscriptIds(new Set(transcripts.map((t) => t.id)))
  }

  const toggleTranscript = (id: number) => {
    setSelectedTranscriptIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleGenerate = async () => {
    if (!dealId) return
    setLoading(true)
    setResult(null)
    setApplied(new Set())

    // Combine manual notes + selected transcripts
    const transcriptTexts = savedTranscripts
      .filter((t) => selectedTranscriptIds.has(t.id))
      .map((t) => `## Transcript: ${t.title} (${t.call_date})\n${t.transcript}`)
    const combinedNotes = [notes.trim(), ...transcriptTexts].filter(Boolean).join('\n\n---\n\n')

    try {
      const res = await scopeVersionsApi.blueprintSuggest(dealId, combinedNotes, includeExcerpt || includeHubspot)
      if (res.success && res.data) {
        setResult(res.data)
      } else {
        setResult({ suggestions: {}, confidence: 'low', reasoning: res.error || 'Failed to generate suggestions', warnings: [] })
      }
    } catch {
      setResult({ suggestions: {}, confidence: 'low', reasoning: 'Network error — could not reach server', warnings: [] })
    }
    setLoading(false)
  }

  const hasInput = notes.trim() || selectedTranscriptIds.size > 0 || includeHubspot || (includeExcerpt && hubspotExcerpt)

  const applyField = (field: BlueprintApplyField, value: string, summary?: string) => {
    onApply(field, value)
    const summaryField = SUMMARY_FIELD_MAP[field]
    if (summaryField && summary) {
      onApply(summaryField, summary)
    }
    setApplied((prev) => new Set([...prev, field]))
  }

  const dq = result?.suggestions.discoveryQuestions
  const hasDiscoveryAnswers = dq && Object.values(dq).some((v) => parseAiField(v as AiTextField | string | null))
  const hasSuggestions = result && (
    parseAiField(result.suggestions.businessModel) || parseAiField(result.suggestions.monthlyOnboardingVolume) || parseAiField(result.suggestions.strategicPriorities)
    || parseAiField(result.suggestions.stpRate) || parseAiField(result.suggestions.fraudRate)
    || parseAiField(result.suggestions.problemHypothesis) || parseAiField(result.suggestions.surfacePain) || parseAiField(result.suggestions.deepPain) || parseAiField(result.suggestions.whyNow)
    || parseAiField(result.suggestions.regulatoryContext) || parseAiField(result.suggestions.regulatoryIntel)
    || parseAiField(result.suggestions.currentProcess)
    || (result.suggestions.stakeholders && result.suggestions.stakeholders.length > 0)
    || (result.suggestions.countryRulesets && result.suggestions.countryRulesets.length > 0)
    || (result.suggestions.verificationStack && result.suggestions.verificationStack.length > 0)
    || (result.suggestions.maturityDimensions && result.suggestions.maturityDimensions.length > 0)
    || (result.suggestions.priorityGaps && result.suggestions.priorityGaps.length > 0)
    || (result.suggestions.quickWins && result.suggestions.quickWins.length > 0)
    || (result.suggestions.foundationMoves && result.suggestions.foundationMoves.length > 0)
    || (result.suggestions.strategicShifts && result.suggestions.strategicShifts.length > 0)
    || hasDiscoveryAnswers
  )

  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded-lg hover:bg-violet-500/20 transition-colors">
        <Sparkles className="w-3.5 h-3.5" />
        AI Assist
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setOpen(false)} />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-3xl bg-[var(--bg-secondary)] rounded-lg shadow-modal" role="dialog" aria-modal="true">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-violet-500/20 bg-violet-500/5 rounded-t-lg">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-400" />
                  <span className="text-sm font-semibold text-violet-400">AI-Assisted Blueprint</span>
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-violet-500/20 text-violet-300 rounded">SUGGESTIONS ONLY</span>
                </div>
                <button onClick={() => setOpen(false)} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 max-h-[calc(100vh-10rem)] overflow-y-auto">
                <p className="text-xs text-[var(--text-secondary)]">
                  Select saved transcripts, paste additional notes, or both. The AI will analyse this alongside HubSpot data to <strong>suggest</strong> content for all blueprint sections — context, metrics, problem hypothesis, verification stack, maturity assessment, priority gaps, recommended path, stakeholders, and country rulesets. You review and decide what to use.
                </p>

                <TranscriptSourcePanel
                  dealId={dealId}
                  isActive={open}
                  selectedTranscriptIds={selectedTranscriptIds}
                  onToggleTranscript={toggleTranscript}
                  onTranscriptsLoaded={handleTranscriptsLoaded}
                  includeExcerpt={includeExcerpt}
                  onToggleExcerpt={setIncludeExcerpt}
                  hubspotExcerpt={hubspotExcerpt}
                  onExcerptChange={setHubspotExcerpt}
                  excerptLoading={excerptLoading}
                  onExcerptLoadingChange={setExcerptLoading}
                />

                {/* Additional notes */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Additional Notes (optional)</p>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Paste extra context — email threads, Slack messages, or anything not in a saved transcript..."
                    className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y"
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-between">
                  {!hubspotExcerpt && (
                    <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)] cursor-pointer">
                      <input type="checkbox" checked={includeHubspot} onChange={(e) => setIncludeHubspot(e.target.checked)}
                        className="rounded border-[var(--border-color)]" />
                      Pull raw context from HubSpot (notes, deal properties)
                    </label>
                  )}
                  {hubspotExcerpt && <div />}
                  <button
                    onClick={handleGenerate}
                    disabled={loading || !dealId || !hasInput}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysing...</> : <><Sparkles className="w-3.5 h-3.5" /> Generate suggestions</>}
                  </button>
                </div>

                {!dealId && (
                  <p className="text-[11px] text-amber-400">Link this scope to a deal first to enable AI suggestions.</p>
                )}

                {/* Results */}
                {result && (
                  <div className="space-y-4 pt-3 border-t border-violet-500/20">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                        result.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400'
                        : result.confidence === 'medium' ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-red-500/20 text-red-400'
                      }`}>
                        {result.confidence} confidence
                      </span>
                      <span className="px-1.5 py-0.5 text-[9px] font-medium bg-[var(--bg-tertiary)] text-[var(--text-muted)] rounded">
                        via Cassidy AI
                      </span>
                      <span className="text-[11px] text-[var(--text-muted)]">{result.reasoning}</span>
                    </div>

                    {result.warnings.length > 0 && (
                      <div className="text-[11px] text-amber-400">
                        {result.warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
                      </div>
                    )}

                    {/* Context & Metrics suggestions */}
                    {(parseAiField(result.suggestions.businessModel) || parseAiField(result.suggestions.monthlyOnboardingVolume) || parseAiField(result.suggestions.strategicPriorities) || parseAiField(result.suggestions.stpRate) || parseAiField(result.suggestions.fraudRate)) && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-semibold text-cyan-400 uppercase tracking-wide flex items-center gap-1.5">
                          <Building2 className="w-3 h-3" /> Context & Current Metrics
                        </p>
                        {(['businessModel', 'monthlyOnboardingVolume', 'strategicPriorities', 'stpRate', 'fraudRate'] as const).map((field) => {
                          const parsed = parseAiField(result.suggestions[field])
                          if (!parsed) return null
                          const labels: Record<string, string> = { businessModel: 'Business Model', monthlyOnboardingVolume: 'Monthly Onboarding Volume', strategicPriorities: 'Strategic Priorities', stpRate: 'STP Rate', fraudRate: 'Fraud Rate' }
                          const isApplied = applied.has(field)
                          const sc = STRENGTH_CONFIG[parsed.strength]
                          const currentVal = currentBlueprint[field as keyof BlueprintConfig] as string
                          return (
                            <div key={field} className={`rounded-lg border p-3 ${isApplied ? 'border-emerald-500/30 bg-emerald-500/5' : !sc.actionable ? 'border-amber-500/20 bg-amber-500/5' : 'border-[var(--border-color)] bg-[var(--bg-primary)]'}`}>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{labels[field]}</span>
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${sc.bg} ${sc.color} border ${sc.border}`}>{sc.label}</span>
                                </div>
                                {isApplied ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>
                                ) : sc.actionable ? (
                                  <button onClick={() => applyField(field, parsed.text, parsed.summary)} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded hover:bg-violet-500/20 transition-colors">Use this suggestion</button>
                                ) : null}
                              </div>
                              {parsed.summary && <p className="text-[10px] text-[var(--text-muted)] mb-1 italic">Summary: {parsed.summary}</p>}
                              <p className={`text-xs whitespace-pre-wrap ${!sc.actionable ? 'text-[var(--text-muted)] italic' : 'text-[var(--text-secondary)]'}`}>{parsed.text}</p>
                              <CurrentValueDrawer value={currentVal} label={labels[field]} />
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Problem Hypothesis suggestions */}
                    {(result.suggestions.problemHypothesis || result.suggestions.surfacePain || result.suggestions.deepPain || result.suggestions.whyNow) && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-semibold text-violet-400 uppercase tracking-wide flex items-center gap-1.5">
                          <Brain className="w-3 h-3" /> Problem Hypothesis
                        </p>
                        {(['problemHypothesis', 'surfacePain', 'deepPain', 'whyNow'] as const).map((field) => {
                          const parsed = parseAiField(result.suggestions[field])
                          if (!parsed) return null
                          const labels = { problemHypothesis: 'Problem Hypothesis', surfacePain: 'Surface Pain', deepPain: 'Deep Pain', whyNow: 'Why Now?' }
                          const isApplied = applied.has(field)
                          const sc = STRENGTH_CONFIG[parsed.strength]
                          const currentVal = currentBlueprint[field]
                          return (
                            <div key={field} className={`rounded-lg border p-3 ${isApplied ? 'border-emerald-500/30 bg-emerald-500/5' : !sc.actionable ? 'border-amber-500/20 bg-amber-500/5' : 'border-[var(--border-color)] bg-[var(--bg-primary)]'}`}>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">{labels[field]}</span>
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${sc.bg} ${sc.color} border ${sc.border}`}>{sc.label}</span>
                                </div>
                                {isApplied ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>
                                ) : sc.actionable ? (
                                  <button
                                    onClick={() => applyField(field, parsed.text, parsed.summary)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded hover:bg-violet-500/20 transition-colors"
                                  >
                                    Use this suggestion
                                  </button>
                                ) : null}
                              </div>
                              {!sc.actionable && !isApplied && <p className="text-xs font-semibold text-amber-400 mb-1">More information required</p>}
                              {parsed.summary && <p className="text-[10px] text-[var(--text-muted)] mb-1 italic">Summary: {parsed.summary}</p>}
                              <p className={`text-xs whitespace-pre-wrap ${!sc.actionable ? 'text-[var(--text-muted)] italic' : 'text-[var(--text-secondary)]'}`}>{parsed.text}</p>
                              <CurrentValueDrawer value={currentVal} label={labels[field]} />
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Regulatory & Industry Context suggestions */}
                    {(result.suggestions.regulatoryContext || result.suggestions.regulatoryIntel) && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-semibold text-rose-400 uppercase tracking-wide flex items-center gap-1.5">
                          <Shield className="w-3 h-3" /> Regulatory & Industry Context
                        </p>
                        {(() => {
                          const parsed = parseAiField(result.suggestions.regulatoryContext)
                          if (!parsed) return null
                          const isApplied = applied.has('regulatoryContext')
                          const sc = STRENGTH_CONFIG[parsed.strength]
                          // Regulatory context is always actionable — it's inherently inferential (industry knowledge, reading between the lines)
                          // so it will often come back as "moderate", but that doesn't mean it needs more info like other fields do
                          return (
                            <div className={`rounded-lg border p-3 ${isApplied ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-[var(--border-color)] bg-[var(--bg-primary)]'}`}>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Regulatory Context</span>
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${sc.bg} ${sc.color} border ${sc.border}`}>{parsed.strength === 'strong' ? 'Strong evidence' : 'AI inference'}</span>
                                </div>
                                {isApplied ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>
                                ) : (
                                  <button
                                    onClick={() => {
                                      applyField('regulatoryContext', parsed.text, parsed.summary)
                                      // Also apply regulatoryIntel if available
                                      const intel = parseAiField(result.suggestions.regulatoryIntel)
                                      if (intel) {
                                        onApply('regulatoryIntel', intel.text)
                                        setApplied((prev) => new Set([...prev, 'regulatoryIntel']))
                                      }
                                    }}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-rose-400 bg-rose-500/10 border border-rose-500/25 rounded hover:bg-rose-500/20 transition-colors"
                                  >
                                    Use this suggestion
                                  </button>
                                )}
                              </div>
                              {parsed.summary && <p className="text-[10px] text-[var(--text-muted)] mb-1 italic">Summary: {parsed.summary}</p>}
                              <p className="text-xs whitespace-pre-wrap text-[var(--text-secondary)]">{parsed.text}</p>
                              <CurrentValueDrawer value={currentBlueprint.regulatoryContext} label="Regulatory Context" />
                            </div>
                          )
                        })()}
                        {(() => {
                          const intel = parseAiField(result.suggestions.regulatoryIntel)
                          if (!intel) return null
                          const isApplied = applied.has('regulatoryIntel')
                          return (
                            <div className={`rounded-lg border p-3 ${isApplied ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-rose-500/20 bg-rose-500/5'}`}>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">AI Intel (hidden — SE only)</span>
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/25">Internal</span>
                                </div>
                                {isApplied && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>}
                              </div>
                              <p className="text-xs whitespace-pre-wrap text-[var(--text-muted)]">{intel.text}</p>
                            </div>
                          )
                        })()}
                      </div>
                    )}

                    {/* Stakeholder suggestions */}
                    {result.suggestions.stakeholders && result.suggestions.stakeholders.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-semibold text-violet-400 uppercase tracking-wide flex items-center gap-1.5">
                          <Users className="w-3 h-3" /> Suggested Stakeholders
                        </p>
                        {result.suggestions.stakeholders.map((s, i) => {
                          const key = `stakeholder-${i}`
                          const isApplied = applied.has(key)
                          const engLabel = ENGAGEMENT_STATES.find((e) => e.value === s.engagementState)?.label || s.engagementState
                          const engColor =
                            s.engagementState === 'champion' ? 'bg-emerald-500/20 text-emerald-400'
                            : s.engagementState === 'champion_candidate' ? 'bg-amber-500/20 text-amber-400'
                            : s.engagementState === 'blocker' ? 'bg-red-500/20 text-red-400'
                            : s.engagementState === 'evaluator' ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)]'
                          return (
                            <div key={key} className={`rounded-lg border p-3 ${isApplied ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-[var(--border-color)] bg-[var(--bg-primary)]'}`}>
                              <div className="flex items-start justify-between gap-3 mb-1.5">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                  <span className="text-xs font-semibold text-[var(--text-primary)]">{s.name}</span>
                                  <span className="text-[10px] text-[var(--text-secondary)]">{s.role}</span>
                                  <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${engColor}`}>{engLabel}</span>
                                </div>
                                <div className="shrink-0">
                                  {isApplied ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Added</span>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        onAddStakeholder(s)
                                        setApplied((prev) => new Set([...prev, key]))
                                      }}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded hover:bg-violet-500/20 transition-colors whitespace-nowrap"
                                    >
                                      Add stakeholder
                                    </button>
                                  )}
                                </div>
                              </div>
                              {s.technicalConcerns && (
                                <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap">{s.technicalConcerns}</p>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Country ruleset suggestions */}
                    {result.suggestions.countryRulesets && result.suggestions.countryRulesets.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-semibold text-violet-400 uppercase tracking-wide flex items-center gap-1.5">
                          <Globe className="w-3 h-3" /> Suggested Country Rulesets
                        </p>
                        {result.suggestions.countryRulesets.map((c, i) => {
                          const key = `country-${i}`
                          const isApplied = applied.has(key)
                          const countryLabel = COUNTRIES.find((co) => co.code === c.country)?.name || c.country
                          const rulesetLabel = RULESET_OPTIONS.find((r) => r.value === c.ruleset)?.label || c.ruleset
                          const rulesetColor =
                            c.ruleset === 'unknown' ? 'bg-orange-500/20 text-orange-400'
                            : c.ruleset === 'custom' ? 'bg-violet-500/20 text-violet-400'
                            : 'bg-blue-500/20 text-blue-400'
                          const hasVendors = (c.docScanVendors?.length || 0) + (c.selfieVendors?.length || 0) + (c.deviceDataVendors?.length || 0) > 0
                          const hasRates = c.passRate || c.desiredPassRate
                          return (
                            <div key={key} className={`rounded-lg border p-3 space-y-2 ${isApplied ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-[var(--border-color)] bg-[var(--bg-primary)]'}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                  <span className="text-xs font-semibold text-[var(--text-primary)]">{countryLabel}</span>
                                  <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${rulesetColor}`}>{rulesetLabel}</span>
                                </div>
                                <div className="shrink-0">
                                  {isApplied ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Added</span>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        onAddCountry(c)
                                        setApplied((prev) => new Set([...prev, key]))
                                      }}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded hover:bg-violet-500/20 transition-colors whitespace-nowrap"
                                    >
                                      Add country
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Detail pills */}
                              {(hasVendors || hasRates || (c.nationalIds && c.nationalIds.length > 0)) && (
                                <div className="flex flex-wrap gap-1.5">
                                  {c.passRate && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">Pass rate: {c.passRate}</span>
                                  )}
                                  {c.desiredPassRate && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-emerald-500/10 text-emerald-400">Target: {c.desiredPassRate}</span>
                                  )}
                                  {c.selfieVendors && c.selfieVendors.length > 0 && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">Selfie: {c.selfieVendors.join(', ')}</span>
                                  )}
                                  {c.docScanVendors && c.docScanVendors.length > 0 && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">Doc scan: {c.docScanVendors.join(', ')}</span>
                                  )}
                                  {c.deviceDataVendors && c.deviceDataVendors.length > 0 && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">Device: {c.deviceDataVendors.join(', ')}</span>
                                  )}
                                  {c.nationalIds && c.nationalIds.length > 0 && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">IDs: {c.nationalIds.join(', ')}</span>
                                  )}
                                </div>
                              )}

                              {/* Data inputs */}
                              {c.dataInputs && c.dataInputs.filter(d => d.enabled).length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  <span className="text-[9px] text-[var(--text-muted)] font-medium mr-1">Data inputs:</span>
                                  {c.dataInputs.filter(d => d.enabled).map((d) => {
                                    const fuzzColors: Record<string, string> = { strict: 'bg-red-500/15 text-red-400', moderate: 'bg-amber-500/15 text-amber-400', fuzzy: 'bg-emerald-500/15 text-emerald-400', optional: 'bg-blue-500/15 text-blue-400' }
                                    return (
                                      <span key={d.type} className={`px-1.5 py-0.5 text-[9px] font-medium rounded ${fuzzColors[d.fuzziness || ''] || 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)]'}`}>
                                        {d.type}{d.fuzziness && d.fuzziness !== 'unknown' ? ` (${d.fuzziness})` : ''}
                                      </span>
                                    )
                                  })}
                                </div>
                              )}

                              {c.notes && (
                                <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap">{c.notes}</p>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Current Process suggestion */}
                    {result.suggestions.currentProcess && (() => {
                      const parsed = parseAiField(result.suggestions.currentProcess)
                      if (!parsed) return null
                      const isApplied = applied.has('currentProcess')
                      const sc = STRENGTH_CONFIG[parsed.strength]
                      const currentVal = currentBlueprint.currentProcess
                      return (
                        <div className="space-y-2">
                          <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wide flex items-center gap-1.5">
                            <Layers className="w-3 h-3" /> Current Verification Process
                          </p>
                          <div className={`rounded-lg border p-3 ${isApplied ? 'border-emerald-500/30 bg-emerald-500/5' : !sc.actionable ? 'border-amber-500/20 bg-amber-500/5' : 'border-[var(--border-color)] bg-[var(--bg-primary)]'}`}>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Current Process</span>
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${sc.bg} ${sc.color} border ${sc.border}`}>{sc.label}</span>
                              </div>
                              {isApplied ? (
                                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>
                              ) : sc.actionable ? (
                                <button
                                  onClick={() => applyField('currentProcess', parsed.text, parsed.summary)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-violet-400 bg-violet-500/10 border border-violet-500/25 rounded hover:bg-violet-500/20 transition-colors"
                                >
                                  Use this suggestion
                                </button>
                              ) : null}
                            </div>
                            {!sc.actionable && !isApplied && <p className="text-xs font-semibold text-amber-400 mb-1">More information required</p>}
                            {parsed.summary && <p className="text-[10px] text-[var(--text-muted)] mb-1 italic">Summary: {parsed.summary}</p>}
                            <p className={`text-xs whitespace-pre-wrap ${!sc.actionable ? 'text-[var(--text-muted)] italic' : 'text-[var(--text-secondary)]'}`}>{parsed.text}</p>
                            <CurrentValueDrawer value={currentVal} label="Current Process" />
                          </div>
                        </div>
                      )
                    })()}

                    {/* Discovery question suggestions */}
                    {hasDiscoveryAnswers && dq && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wide flex items-center gap-1.5">
                          <AlertCircle className="w-3 h-3" /> Discovery Questions
                        </p>
                        {(Object.entries(dq) as [keyof AiDiscoveryQuestions, AiTextField | string | null | undefined][]).map(([field, rawValue]) => {
                          const parsed = parseAiField(rawValue)
                          if (!parsed) return null
                          const label = DISCOVERY_QUESTIONS.find((q) => q.field === field)?.question || field
                          const isApplied = applied.has(field)
                          const sc = STRENGTH_CONFIG[parsed.strength]
                          const currentVal = currentBlueprint[field as keyof BlueprintConfig] as string
                          return (
                            <div key={field} className={`rounded-lg border p-3 ${isApplied ? 'border-emerald-500/30 bg-emerald-500/5' : !sc.actionable ? 'border-amber-500/20 bg-amber-500/5' : 'border-blue-500/20 bg-blue-500/5'}`}>
                              <div className="flex items-start justify-between gap-3 mb-1.5">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                  <p className="text-[11px] font-medium text-blue-300">{label}</p>
                                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${sc.bg} ${sc.color} border ${sc.border}`}>{sc.label}</span>
                                </div>
                                <div className="shrink-0">
                                  {isApplied ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>
                                  ) : sc.actionable ? (
                                    <button
                                      onClick={() => applyField(field, parsed.text)}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-blue-400 bg-blue-500/10 border border-blue-500/25 rounded hover:bg-blue-500/20 transition-colors whitespace-nowrap"
                                    >
                                      Use this
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                              {!sc.actionable && !isApplied && <p className="text-xs font-semibold text-amber-400 mb-1">More information required</p>}
                              <p className={`text-xs whitespace-pre-wrap ${!sc.actionable ? 'text-[var(--text-muted)] italic' : 'text-[var(--text-secondary)]'}`}>{parsed.text}</p>
                              <CurrentValueDrawer value={currentVal} label={label} />
                            </div>
                          )
                        })}
                      </div>
                    )}

                    {/* Verification Stack suggestions */}
                    {result.suggestions.verificationStack && result.suggestions.verificationStack.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-semibold text-teal-400 uppercase tracking-wide flex items-center gap-1.5">
                            <Layers className="w-3 h-3" /> Verification Stack
                          </p>
                          {applied.has('vstack-all') ? (
                            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-medium"><Check className="w-3 h-3" /> Applied</span>
                          ) : (
                            <button
                              onClick={() => { onReplaceVerificationStack(result.suggestions.verificationStack!); setApplied((prev) => new Set([...prev, 'vstack-all'])) }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-teal-400 bg-teal-500/10 border border-teal-500/25 rounded hover:bg-teal-500/20 transition-colors whitespace-nowrap"
                            >
                              Use all suggestions
                            </button>
                          )}
                        </div>
                        <p className="text-[10px] text-[var(--text-muted)]">This will replace all existing verification stack entries with the AI suggestions below.</p>
                        {result.suggestions.verificationStack.map((v, i) => (
                          <div key={`vstack-${i}`} className={`rounded-lg border p-3 ${applied.has('vstack-all') ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-[var(--border-color)] bg-[var(--bg-primary)]'}`}>
                            <div className="flex items-center gap-2 flex-wrap min-w-0 mb-1">
                              <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-teal-500/20 text-teal-400">{v.layer}</span>
                              <span className="text-xs font-semibold text-[var(--text-primary)]">{v.vendor}</span>
                              <span className="text-[10px] text-[var(--text-muted)]">({v.owner})</span>
                            </div>
                            {v.painPoints && <p className="text-xs text-[var(--text-secondary)]">{v.painPoints}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {!hasSuggestions && (
                      <p className="text-xs text-[var(--text-muted)] italic py-2">No suggestions could be generated from the provided context. Try adding more detailed call notes.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

// --- Blueprint Cascade Generator ---
function BlueprintCascadeGenerator({ dealId, dealName, countryRulesets }: {
  dealId: number
  dealName: string
  countryRulesets: Array<{ id: number; country: string; ruleset: string }>
}) {
  const [generating, setGenerating] = useState(false)
  const [progress, setProgress] = useState('')
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set())
  const cascadeStore = useCascadeStore

  const countriesWithNames = countryRulesets
    .filter(cr => cr.country)
    .map(cr => {
      const match = COUNTRIES.find(c => c.code === cr.country)
      return match ? { country: match.name, countryCode: match.code } : null
    })
    .filter(Boolean)
    .filter((c, i, arr) => arr.findIndex(x => x!.countryCode === c!.countryCode) === i) as Array<{ country: string; countryCode: string }>

  // No auto-selection — user opts in to countries they want to generate

  if (countriesWithNames.length === 0) return null

  const toggleCountry = (code: string) => {
    setSelectedCountries(prev => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  const selectedList = countriesWithNames.filter(c => selectedCountries.has(c.countryCode))

  const handleGenerate = async () => {
    if (selectedList.length === 0) return
    setGenerating(true)
    setProgress(`Generating cascades for ${selectedList.length} ${selectedList.length === 1 ? 'country' : 'countries'}...`)
    console.log('[BlueprintCascadeGenerator] Starting generation for:', selectedList)

    try {
      const { context } = await fetchBlueprintContext(dealId)
      console.log('[BlueprintCascadeGenerator] Blueprint context loaded, calling AI...')

      const res = await cascadeApi.aiGenerateMulti({
        countries: selectedList,
        dealId,
        blueprintContext: context,
        includeHubspot: true,
      })

      console.log('[BlueprintCascadeGenerator] Response:', res.success, res.error)

      if (!res.success || !res.data) {
        showToast(res.error || 'Failed to generate cascades', 'error')
        return
      }

      const { cascades, errors } = res.data

      if (cascades.length === 0) {
        showToast('No cascades generated. ' + (errors.length > 0 ? errors[0].error : ''), 'error')
        return
      }

      // Deduplicate cascades by country code — keep the one with the most nodes (most complete)
      const dedupedCascades = cascades.reduce((acc, c) => {
        const existing = acc.find(x => x.countryCode === c.countryCode)
        if (!existing) {
          acc.push(c)
        } else if ((c.nodes as unknown[]).length > (existing.nodes as unknown[]).length) {
          acc[acc.indexOf(existing)] = c
        }
        return acc
      }, [] as typeof cascades)

      // Load into cascade store — set deal, first as active, rest as countryCascades
      const first = dedupedCascades[0]
      const rest = dedupedCascades.slice(1)

      cascadeStore.setState({
        dealId,
        dealName,
        country: first.country,
        countryCode: first.countryCode,
        nodes: first.nodes as CascadeNode[],
        edges: first.edges as CascadeEdge[],
        notes: first.notes || '',
        countryCascades: rest.map(c => ({
          country: c.country,
          countryCode: c.countryCode,
          nodes: c.nodes as CascadeNode[],
          edges: c.edges as CascadeEdge[],
          notes: c.notes || '',
        })),
        selectedNodeId: null,
        activeChoice: null,
        fadingChoiceIds: [],
      })

      // Auto-save the project
      setTimeout(() => cascadeStore.getState().saveProject(), 100)

      const msg = `Generated ${cascades.length} cascade${cascades.length > 1 ? 's' : ''}`
      if (errors.length > 0) {
        showToast(`${msg} (${errors.length} failed)`, 'warning')
      } else {
        showToast(`${msg} — saved to Cascade Builder`, 'success')
      }
    } catch (error) {
      console.error('Blueprint cascade generation error:', error)
      showToast('Failed to generate cascades', 'error')
    } finally {
      setGenerating(false)
      setProgress('')
    }
  }

  return (
    <div className="mb-4 p-3 rounded-lg border border-violet-500/20 bg-violet-500/5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-400" />
          <span className="text-xs font-medium text-violet-400">AI Generate Cascades</span>
        </div>
        <div className="flex items-center gap-2">
          {selectedList.length < countriesWithNames.length && (
            <button
              onClick={() => setSelectedCountries(new Set(countriesWithNames.map(c => c.countryCode)))}
              className="text-[10px] text-violet-400 hover:underline"
            >
              Select all
            </button>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating || selectedList.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-violet-600 hover:bg-violet-500 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-wait"
          >
            {generating ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> {progress || 'Generating...'}</>
            ) : selectedList.length === 0 ? (
              <><Sparkles className="w-3 h-3" /> Select countries below</>
            ) : (
              <><Sparkles className="w-3 h-3" /> Generate {selectedList.length} Cascade{selectedList.length !== 1 ? 's' : ''}</>
            )}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {countriesWithNames.map(c => {
          const selected = selectedCountries.has(c.countryCode)
          return (
            <button
              key={c.countryCode}
              onClick={() => toggleCountry(c.countryCode)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-colors text-[11px] font-medium ${
                selected
                  ? 'border-violet-500/40 bg-violet-500/15 text-violet-300'
                  : 'border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-muted)] hover:bg-[var(--bg-tertiary)]'
              }`}
            >
              {selected ? '✓' : '+'} {c.countryCode} — {c.country}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// --- Main component ---
interface BlueprintTabProps {
  dealId: number | null
}

const DISCOVERY_FIELD_NAMES: Set<string> = new Set(DISCOVERY_QUESTIONS.map((q) => q.field))

export function BlueprintTab({ dealId }: BlueprintTabProps) {
  const cascadeSavedProjects = useCascadeStore((s) => s.savedProjects)
  const linkedCascades = cascadeSavedProjects.filter((p) => p.dealId === dealId && dealId !== null)
  const {
    scope,
    updateBlueprintField,
    addBlueprintCountryRuleset, updateBlueprintCountryRuleset, removeBlueprintCountryRuleset, toggleBlueprintCountryInputData, updateBlueprintCountryInputFuzziness, updateBlueprintCountryInputCaptureMethod, updateBlueprintCountryInputField,
    addBlueprintStakeholder, updateBlueprintStakeholder, removeBlueprintStakeholder,
    toggleBlueprintPrepCheck, resetBlueprintPrepChecklist,
    updateBlueprintVerificationLayer, addBlueprintVerificationLayer, removeBlueprintVerificationLayer,
  } = useScopeStore()
  const bp = scope.blueprintConfig
  const [editingContext, setEditingContext] = useState(false)
  const [editingHypothesis, setEditingHypothesis] = useState(false)
  const [editingStack, setEditingStack] = useState(false)
  const [editingStakeholders, setEditingStakeholders] = useState(false)
  const [expandedPriorities, setExpandedPriorities] = useState(false)
  const [expandedProblem, setExpandedProblem] = useState(false)
  const [discoveryHighlights, setDiscoveryHighlights] = useState<Set<string>>(new Set())

  // Sync height between paired textareas (business model / strategic priorities)
  const bizModelRef = useRef<HTMLTextAreaElement>(null)
  const stratPriRef = useRef<HTMLTextAreaElement>(null)
  const syncTextareaHeights = useCallback(() => {
    const a = bizModelRef.current
    const b = stratPriRef.current
    if (!a || !b) return
    // Reset both to auto to get natural height
    a.style.height = 'auto'
    b.style.height = 'auto'
    const maxH = Math.max(a.scrollHeight, b.scrollHeight)
    a.style.height = `${maxH}px`
    b.style.height = `${maxH}px`
  }, [])

  // Re-sync when blueprint content changes
  useEffect(() => {
    syncTextareaHeights()
  }, [bp.businessModel, bp.strategicPriorities, syncTextareaHeights])

  // Load cascade projects from DB when dealId is available
  useEffect(() => {
    if (dealId) {
      useCascadeStore.getState().loadProjectsFromDb(dealId)
    }
  }, [dealId])

  const handleAiApply = useCallback((field: BlueprintApplyField, value: string) => {
    updateBlueprintField(field, value)
    if (DISCOVERY_FIELD_NAMES.has(field)) {
      setDiscoveryHighlights(new Set([field]))
    }
  }, [updateBlueprintField])

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">Pre-Sales Blueprint</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {"Map the customer's as-is state before designing the to-be. Blueprinting is the discipline of deeply understanding the customer's current architecture, not just their surface-level pain."}
          </p>
        </div>
        <BlueprintSuggestModal
          dealId={dealId}
          currentBlueprint={bp}
          onApply={handleAiApply}
          onAddStakeholder={(s) => {
            // Check for existing stakeholder with the same name (case-insensitive) — update instead of duplicating
            const existing = useScopeStore.getState().scope.blueprintConfig.stakeholders.find(
              (st) => st.name.trim().toLowerCase() === s.name.trim().toLowerCase()
            )
            if (existing) {
              updateBlueprintStakeholder(existing.id, {
                role: s.role || existing.role,
                technicalConcerns: s.technicalConcerns || existing.technicalConcerns,
                engagementState: s.engagementState || existing.engagementState,
              })
            } else {
              addBlueprintStakeholder()
              const latest = useScopeStore.getState().scope.blueprintConfig.stakeholders
              const newest = latest[latest.length - 1]
              if (newest) {
                updateBlueprintStakeholder(newest.id, {
                  name: s.name,
                  role: s.role,
                  technicalConcerns: s.technicalConcerns,
                  engagementState: s.engagementState,
                })
              }
            }
          }}
          onAddCountry={(c) => {
            const validRulesets = ['1+1', '2+2', 'custom', 'unknown'] as const
            const ruleset = validRulesets.includes(c.ruleset as any) ? c.ruleset as typeof validRulesets[number] : 'unknown'
            // Normalize country to ISO code — AI might return full name like "Australia" instead of "AU"
            let countryCode = c.country || ''
            // Try exact code match first
            if (countryCode && !COUNTRIES.find(ct => ct.code === countryCode)) {
              // Not a valid ISO code — try matching by name
              const match = COUNTRIES.find(ct => ct.name.toLowerCase() === countryCode.toLowerCase())
              if (match) countryCode = match.code
            }
            // If still not valid after normalization, try partial match
            if (countryCode && !COUNTRIES.find(ct => ct.code === countryCode)) {
              const partial = COUNTRIES.find(ct => ct.name.toLowerCase().includes(countryCode.toLowerCase()) || countryCode.toLowerCase().includes(ct.name.toLowerCase()))
              if (partial) countryCode = partial.code
            }
            console.log(`[AI Country] input="${c.country}" → normalized="${countryCode}"`)
            if (!countryCode) return // Skip if we can't resolve the country
            // Check if country already exists — update it instead of adding a duplicate
            const existing = useScopeStore.getState().scope.blueprintConfig.countryRulesets.find(
              (cr) => cr.country === countryCode
            )
            const validFuzz = ['strict', 'moderate', 'fuzzy', 'optional', 'unknown'] as const
            const mergeDataInputs = (baseInputs: typeof getDefaultDataInputs extends () => infer R ? R : never, aiInputs?: typeof c.dataInputs) => {
              return baseInputs.map((di) => {
                const aiInput = aiInputs?.find((ai) => ai.type === di.type)
                if (aiInput && aiInput.enabled) {
                  return { ...di, enabled: true, fuzziness: validFuzz.includes(aiInput.fuzziness as any) ? aiInput.fuzziness as typeof validFuzz[number] : 'unknown' as const, captureMethod: aiInput.captureMethod || di.captureMethod || '', notes: aiInput.notes || di.notes || '' }
                }
                return di
              })
            }
            if (existing) {
              // Merge into existing country ruleset
              updateBlueprintCountryRuleset(existing.id, {
                ruleset: existing.ruleset === 'unknown' ? ruleset : existing.ruleset,
                notes: c.notes || existing.notes,
                docScanVendors: c.docScanVendors?.length ? c.docScanVendors : existing.docScanVendors,
                selfieVendors: c.selfieVendors?.length ? c.selfieVendors : existing.selfieVendors,
                deviceDataVendors: c.deviceDataVendors?.length ? c.deviceDataVendors : existing.deviceDataVendors,
                nationalIds: c.nationalIds?.length ? c.nationalIds : existing.nationalIds,
                passRate: c.passRate || existing.passRate,
                desiredPassRate: c.desiredPassRate || existing.desiredPassRate,
                dataInputs: mergeDataInputs(existing.dataInputs, c.dataInputs),
              })
            } else {
              // Add new country — use addBlueprintCountryRuleset then immediately set country
              addBlueprintCountryRuleset()
              const latest = useScopeStore.getState().scope.blueprintConfig.countryRulesets
              const newest = latest[latest.length - 1]
              if (newest) {
                updateBlueprintCountryRuleset(newest.id, {
                  country: countryCode, ruleset, notes: c.notes || '',
                  docScanVendors: c.docScanVendors || [], selfieVendors: c.selfieVendors || [],
                  deviceDataVendors: c.deviceDataVendors || [], nationalIds: c.nationalIds || [],
                  passRate: c.passRate || '', desiredPassRate: c.desiredPassRate || '',
                  dataInputs: mergeDataInputs(newest.dataInputs, c.dataInputs),
                })
              }
            }
          }}
          onReplaceVerificationStack={(layers) => {
            // Replace all existing verification stack entries with AI suggestions
            const stack = useScopeStore.getState().scope.blueprintConfig.verificationStack
            for (const existing of stack) {
              const match = layers.find((l) => l.layer === existing.layer)
              if (match) {
                updateBlueprintVerificationLayer(existing.id, {
                  vendor: match.vendor,
                  owner: match.owner,
                  painPoints: match.painPoints,
                })
              } else {
                // Clear layers the AI didn't mention
                updateBlueprintVerificationLayer(existing.id, {
                  vendor: '',
                  painPoints: '',
                })
              }
            }
          }}
        />
      </div>

      {/* Section: Context & Metrics */}
      <Section
        title="Context & Current Metrics"
        subtitle="Business model, volumes, strategic priorities, and key performance metrics"
        icon={<Building2 className="w-5 h-5" />}
        accentColor="cyan"

        complete={bp.businessModel.trim() !== '' && bp.monthlyOnboardingVolume.trim() !== '' && bp.stpRate.trim() !== ''}
        headerAction={(bp.businessModel.trim() || bp.strategicPriorities.trim() || bp.monthlyOnboardingVolume.trim() || bp.stpRate.trim() || bp.fraudRate.trim()) ? (
          <button
            onClick={() => setEditingContext(!editingContext)}
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md border transition-colors ${
              editingContext
                ? 'text-cyan-400 bg-cyan-500/15 border-cyan-500/30'
                : 'text-[var(--text-muted)] bg-[var(--bg-primary)] border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]'
            }`}
          >
            <Pencil className="w-3 h-3" /> {editingContext ? 'Done' : 'Edit'}
          </button>
        ) : undefined}
      >
        <div className="mt-3">
          {editingContext || (!bp.businessModel.trim() && !bp.strategicPriorities.trim() && !bp.monthlyOnboardingVolume.trim() && !bp.stpRate.trim() && !bp.fraudRate.trim()) ? (
          <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Business Model</label>
              <p className="text-[11px] text-[var(--text-muted)] mt-0.5 mb-1">What does the customer do? Who are their customers?</p>
              <textarea
                ref={bizModelRef}
                className="w-full px-3 py-2 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y"
                rows={2}
                placeholder="e.g. B2C neobank targeting 18-35 demographic, offering savings accounts and crypto trading"
                value={bp.businessModel}
                onChange={(e) => updateBlueprintField('businessModel', e.target.value)}
                onMouseUp={syncTextareaHeights}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Strategic Priorities (next 12 months)</label>
              <p className="text-[11px] text-[var(--text-muted)] mt-0.5 mb-1">What are they focused on? What decisions does this drive?</p>
              <textarea
                ref={stratPriRef}
                className="w-full px-3 py-2 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y"
                rows={2}
                placeholder="e.g. Launching in 3 new APAC markets, reducing onboarding drop-off by 50%, achieving SOC 2 Type II"
                value={bp.strategicPriorities}
                onChange={(e) => updateBlueprintField('strategicPriorities', e.target.value)}
                onMouseUp={syncTextareaHeights}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Monthly Onboarding Volume</label>
            <textarea
              className="w-full mt-1 px-3 py-2 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y"
              rows={2}
              placeholder="e.g. 5,000–10,000 per month, growing 20% QoQ"
              value={bp.monthlyOnboardingVolume}
              onChange={(e) => updateBlueprintField('monthlyOnboardingVolume', e.target.value)}
            />
          </div>

          {/* Key Metrics */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide mb-2 block">Key Metrics</label>
            <div className="grid grid-cols-2 gap-3">
              {/* STP Rate */}
              <div className="flex items-center gap-2 p-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
                <button
                  onClick={() => updateBlueprintField('stpRateConfirmed', !bp.stpRateConfirmed)}
                  className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 border transition-all ${
                    bp.stpRateConfirmed ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-amber-500 border-amber-400 text-white hover:brightness-110'
                  }`}
                  title={bp.stpRateConfirmed ? 'Confirmed by customer' : 'Unconfirmed — click to confirm'}
                >{bp.stpRateConfirmed ? '✓' : '?'}</button>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Straight-Through Pass Rate</span>
                  <div className="flex items-center gap-1 mt-0.5">
                    <input
                      type="text"
                      placeholder="e.g. 72%"
                      value={bp.stpRate}
                      onChange={(e) => updateBlueprintField('stpRate', e.target.value)}
                      className="w-20 px-2 py-0.5 text-sm font-semibold rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                    />
                    <span className="text-[10px] text-[var(--text-muted)]">of onboards pass without manual review</span>
                  </div>
                </div>
              </div>

              {/* Fraud Rate */}
              <div className="flex items-center gap-2 p-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
                <button
                  onClick={() => updateBlueprintField('fraudRateConfirmed', !bp.fraudRateConfirmed)}
                  className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 border transition-all ${
                    bp.fraudRateConfirmed ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-amber-500 border-amber-400 text-white hover:brightness-110'
                  }`}
                  title={bp.fraudRateConfirmed ? 'Confirmed by customer' : 'Unconfirmed — click to confirm'}
                >{bp.fraudRateConfirmed ? '✓' : '?'}</button>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Fraud Rate</span>
                  <div className="flex items-center gap-1 mt-0.5">
                    <input
                      type="text"
                      placeholder="e.g. 2.5%"
                      value={bp.fraudRate}
                      onChange={(e) => updateBlueprintField('fraudRate', e.target.value)}
                      className="w-20 px-2 py-0.5 text-sm font-semibold rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                    />
                    <span className="text-[10px] text-[var(--text-muted)]">of onboarded users suspected fraudulent</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
          ) : (
          <div className="space-y-3">
            {bp.businessModel.trim() && (
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Business Model</label>
                <p className="text-xs text-[var(--text-primary)] whitespace-pre-wrap mt-0.5">{bp.businessModel}</p>
              </div>
            )}
            {bp.monthlyOnboardingVolume.trim() && (
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Monthly Onboarding Volume</label>
                <p className="text-xs text-[var(--text-primary)] whitespace-pre-wrap mt-0.5">{bp.monthlyOnboardingVolume}</p>
              </div>
            )}
            {/* Metrics inline */}
            {(bp.stpRate.trim() || bp.fraudRate.trim()) && (
              <div className="flex items-center gap-4">
                {bp.stpRate.trim() && (
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={`w-3 h-3 rounded-full flex items-center justify-center text-[7px] font-bold border ${
                      bp.stpRateConfirmed ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-amber-500 border-amber-400 text-white'
                    }`}>{bp.stpRateConfirmed ? '✓' : '?'}</span>
                    <span className="text-[10px] text-[var(--text-muted)] uppercase font-semibold">STP</span>
                    <span className="text-sm font-bold text-[var(--text-primary)]">{bp.stpRate.replace(/%/g, '')}%</span>
                  </span>
                )}
                {bp.fraudRate.trim() && (
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={`w-3 h-3 rounded-full flex items-center justify-center text-[7px] font-bold border ${
                      bp.fraudRateConfirmed ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-amber-500 border-amber-400 text-white'
                    }`}>{bp.fraudRateConfirmed ? '✓' : '?'}</span>
                    <span className="text-[10px] text-[var(--text-muted)] uppercase font-semibold">Fraud</span>
                    <span className="text-sm font-bold text-[var(--text-primary)]">{bp.fraudRate.replace(/%/g, '')}%</span>
                  </span>
                )}
              </div>
            )}
            {/* Strategic Priorities — summary / full detail toggle */}
            {bp.strategicPriorities.trim() ? (
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Strategic Priorities</label>
                {(() => {
                  const lines = bp.strategicPriorities.split('\n').filter(Boolean)
                  const summaryLines = bp.strategicPrioritiesSummary?.trim()
                    ? bp.strategicPrioritiesSummary.split('\n').filter(Boolean)
                    : null
                  return expandedPriorities ? (
                    <div className="mt-1">
                      <ul className="space-y-1">
                        {lines.map((l, i) => (
                          <li key={i} className="text-xs text-[var(--text-primary)] flex items-start gap-1.5">
                            <span className="text-cyan-400 mt-0.5 shrink-0">•</span>
                            <span>{l.replace(/^[-•]\s*/, '')}</span>
                          </li>
                        ))}
                      </ul>
                      <button onClick={() => setExpandedPriorities(false)} className="text-[10px] text-cyan-400 mt-1 hover:underline">Show less</button>
                    </div>
                  ) : (
                    <div className="mt-1">
                      <ul className="space-y-0.5">
                        {(summaryLines || lines).map((l, i) => (
                          <li key={i} className="text-xs text-[var(--text-primary)] flex items-start gap-1.5">
                            <span className="text-cyan-400 mt-0.5 shrink-0">•</span>
                            <span>{l.replace(/^[-•]\s*/, '')}</span>
                          </li>
                        ))}
                      </ul>
                      <button onClick={() => setExpandedPriorities(true)} className="text-[10px] text-cyan-400 mt-1 hover:underline">Show full detail</button>
                    </div>
                  )
                })()}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500/5 border border-amber-500/15">
                <AlertCircle className="w-3 h-3 text-amber-400 shrink-0" />
                <span className="text-[10px] text-amber-400 font-medium">Strategic priorities not documented — usually: pass rates, manual review, fraud, CX, or new markets</span>
              </div>
            )}
          </div>
          )}
        </div>
      </Section>

      {/* Section 2: Customer Problem Hypothesis */}
      <Section
        title="Customer Problem Hypothesis"
        subtitle="Our interpretation of the problem — not the customer's words, our analysis"
        icon={<Brain className="w-5 h-5" />}
        accentColor="violet"

        complete={bp.problemHypothesis.trim() !== '' && bp.surfacePain.trim() !== '' && bp.deepPain.trim() !== '' && bp.whyNow.trim() !== '' && bp.regulatoryContext.trim() !== ''}
        headerAction={(bp.problemHypothesis.trim() || bp.surfacePain.trim() || bp.deepPain.trim() || bp.whyNow.trim()) ? (
          <button
            onClick={() => setEditingHypothesis(!editingHypothesis)}
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md border transition-colors ${
              editingHypothesis
                ? 'text-violet-400 bg-violet-500/15 border-violet-500/30'
                : 'text-[var(--text-muted)] bg-[var(--bg-primary)] border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]'
            }`}
          >
            <Pencil className="w-3 h-3" /> {editingHypothesis ? 'Done' : 'Edit'}
          </button>
        ) : undefined}
      >
        <div className="mt-3">
          {editingHypothesis || (!bp.problemHypothesis.trim() && !bp.surfacePain.trim() && !bp.deepPain.trim() && !bp.whyNow.trim()) ? (
          <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Problem Hypothesis</label>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5 mb-1">One sentence: what is their core problem?</p>
            <textarea
              className="w-full px-3 py-2 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y"
              rows={2}
              placeholder="e.g. Hardcoded vendor stack can't be tuned without engineers; they need orchestration to manage vendors, rules, and audit."
              value={bp.problemHypothesis}
              onChange={(e) => updateBlueprintField('problemHypothesis', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Surface Pain</label>
              <p className="text-[11px] text-[var(--text-muted)] mt-0.5 mb-1">What the customer says — symptoms, numbers, quotes.</p>
              <textarea
                className="w-full px-3 py-2 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y"
                rows={2}
                placeholder="e.g. UK pass rate is 65%, AML throws 150 false positives/day, manual review takes 18h."
                value={bp.surfacePain}
                onChange={(e) => updateBlueprintField('surfacePain', e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Deep Pain</label>
              <p className="text-[11px] text-[var(--text-muted)] mt-0.5 mb-1">Root cause — what we need to uncover or prove.</p>
              <textarea
                className="w-full px-3 py-2 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y"
                rows={2}
                placeholder="e.g. Flows, rules, and evidence sit across silos so they can't adapt quickly or make one clear risk decision."
                value={bp.deepPain}
                onChange={(e) => updateBlueprintField('deepPain', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Why Now?</label>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5 mb-1">What's creating urgency — contract renewal, regulatory deadline, board mandate, competitor move?</p>
            <textarea
              className="w-full px-3 py-2 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y"
              rows={2}
              placeholder="e.g. Current vendor contract expires Oct 2026. AUSTRAC enforcement notice received — board wants compliant stack before next audit cycle."
              value={bp.whyNow}
              onChange={(e) => updateBlueprintField('whyNow', e.target.value)}
            />
          </div>

          <div>
            <label className="text-xs font-medium text-rose-400 uppercase tracking-wide flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> Regulatory & Industry Context
            </label>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5 mb-1">Regulatory environment, enforcement trends, and signals that suggest unstated pressure.</p>
            <textarea
              className="w-full px-3 py-2 text-sm rounded-md border border-rose-500/30 bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500 resize-y"
              rows={3}
              placeholder="e.g. Australian gambling sector under AUSTRAC scrutiny — $450M in fines to competitors in last 18 months. Client's urgency and compressed timeline suggest regulatory pressure they haven't disclosed."
              value={bp.regulatoryContext}
              onChange={(e) => updateBlueprintField('regulatoryContext', e.target.value)}
            />
          </div>

          {bp.regulatoryIntel.trim() && (
          <div>
            <label className="text-xs font-medium text-rose-400/70 uppercase tracking-wide flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" /> AI Intel (SE only — not visible to customer)
            </label>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5 mb-1">AI working notes — enforcement actions, legislation, behavioural signals. Edit or add your own notes.</p>
            <textarea
              className="w-full px-3 py-2 text-sm rounded-md border border-rose-500/20 bg-rose-500/5 text-[var(--text-secondary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500/40 resize-y"
              rows={4}
              value={bp.regulatoryIntel}
              onChange={(e) => updateBlueprintField('regulatoryIntel', e.target.value)}
            />
          </div>
          )}
          </div>
          ) : (
          <div className="space-y-2">
            {/* Problem Hypothesis — summary / full detail toggle */}
            {bp.problemHypothesis.trim() ? (
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Problem Hypothesis</label>
                <p className={`text-xs text-[var(--text-primary)] mt-0.5 ${expandedProblem ? 'whitespace-pre-wrap' : ''}`}>
                  {expandedProblem ? bp.problemHypothesis : (bp.problemHypothesisSummary?.trim() || bp.problemHypothesis)}
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-red-500/5 border border-red-500/15">
                <AlertCircle className="w-3 h-3 text-red-400 shrink-0" />
                <span className="text-[10px] text-red-400 font-medium">Problem hypothesis missing — what are we solving?</span>
              </div>
            )}
            {/* Surface & Deep pain side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Surface Pain</label>
                {bp.surfacePain.trim() ? (
                  <p className={`text-xs text-[var(--text-primary)] mt-0.5 ${expandedProblem ? 'whitespace-pre-wrap' : ''}`}>
                    {expandedProblem ? bp.surfacePain : (bp.surfacePainSummary?.trim() || bp.surfacePain)}
                  </p>
                ) : (
                  <p className="text-[10px] text-amber-400 mt-0.5 italic">Not documented</p>
                )}
              </div>
              <div>
                <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Deep Pain</label>
                {bp.deepPain.trim() ? (
                  <p className={`text-xs text-[var(--text-primary)] mt-0.5 ${expandedProblem ? 'whitespace-pre-wrap' : ''}`}>
                    {expandedProblem ? bp.deepPain : (bp.deepPainSummary?.trim() || bp.deepPain)}
                  </p>
                ) : (
                  <p className="text-[10px] text-amber-400 mt-0.5 italic">Not documented — dig deeper on next call</p>
                )}
              </div>
            </div>
            {/* Why Now? */}
            <div>
              <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Why Now?</label>
              {bp.whyNow?.trim() ? (
                <p className={`text-xs text-[var(--text-primary)] mt-0.5 ${expandedProblem ? 'whitespace-pre-wrap' : ''}`}>
                  {expandedProblem ? bp.whyNow : (bp.whyNowSummary?.trim() || bp.whyNow)}
                </p>
              ) : (
                <p className="text-[10px] text-amber-400 mt-0.5 italic">Not documented — what's creating urgency?</p>
              )}
            </div>
            {/* Regulatory & Industry Context */}
            {(bp.regulatoryContext.trim() || bp.regulatoryIntel.trim()) && (
              <div className="mt-1 p-2.5 rounded-lg border border-rose-500/20 bg-rose-500/5">
                <label className="text-[10px] font-semibold text-rose-400 uppercase tracking-wide flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Regulatory & Industry Context
                </label>
                {bp.regulatoryContext.trim() && (
                  <p className={`text-xs text-[var(--text-primary)] mt-0.5 ${expandedProblem ? 'whitespace-pre-wrap' : ''}`}>
                    {expandedProblem ? bp.regulatoryContext : (bp.regulatoryContextSummary?.trim() || bp.regulatoryContext)}
                  </p>
                )}
                {bp.regulatoryIntel.trim() && (
                  <RegulatoryIntelDrawer intel={bp.regulatoryIntel} />
                )}
              </div>
            )}
            {/* Show full detail toggle — always show when there's a summary, or when text is long */}
            {(bp.problemHypothesisSummary?.trim() || bp.surfacePainSummary?.trim() || bp.deepPainSummary?.trim() || bp.whyNowSummary?.trim() || bp.regulatoryContextSummary?.trim() || bp.problemHypothesis.length > 150 || bp.surfacePain.length > 120 || bp.deepPain.length > 120 || bp.whyNow.length > 120 || bp.regulatoryContext.length > 120) && (
              <button onClick={() => setExpandedProblem(!expandedProblem)} className="text-[10px] text-violet-400 hover:underline">
                {expandedProblem ? 'Show less' : 'Show full detail'}
              </button>
            )}
          </div>
          )}
        </div>
      </Section>

      {/* Section 3: As-Is Architecture */}
      <Section
        title="As-Is Architecture"
        subtitle="Current verification flow, integration method, discovery questions, and cascade diagrams"
        icon={<Layers className="w-5 h-5" />}
        accentColor="blue"

        complete={bp.integrationMethod.trim() !== '' || bp.miroLink.trim() !== '' || linkedCascades.length > 0}
      >
        <div className="mt-3 space-y-4">
          {/* Current Verification Process */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Current Verification Process</label>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5 mb-1">How does the customer currently verify and onboard users? What data is collected, what checks run, what tools are used? If we don't know yet, say so.</p>
            <textarea
              className="w-full px-3 py-2 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-y"
              rows={3}
              placeholder="e.g. Users sign up via web form → collect name, DOB, address, email → Equifax 1+1 check via direct API → manual document review for failures → AML screening via ComplyAdvantage → account activated. No IDV/biometrics currently. ~30% manual review rate."
              value={bp.currentProcess}
              onChange={(e) => updateBlueprintField('currentProcess', e.target.value)}
            />
          </div>

          {/* Miro embed */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Miro Board Link</label>
            <div className="flex items-center gap-2 mt-1">
              <Link2 className="w-4 h-4 text-[var(--text-muted)]" />
              <input
                type="text"
                className="flex-1 px-3 py-1.5 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                placeholder="https://miro.com/app/board/..."
                value={bp.miroLink}
                onChange={(e) => updateBlueprintField('miroLink', e.target.value)}
              />
              {bp.miroLink && (
                <a href={bp.miroLink} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
            {bp.miroLink && bp.miroLink.includes('miro.com') && (
              <div className="mt-3 rounded-lg overflow-hidden border border-[var(--border-color)]">
                <iframe
                  src={bp.miroLink.replace('/board/', '/live-embed/').split('?')[0] + '?autoplay=yep'}
                  className="w-full h-[400px]"
                  allow="fullscreen"
                  title="Miro board"
                />
              </div>
            )}
          </div>

          {/* Integration method */}
          <div>
            <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wide">Current Integration Method</label>
            <input
              type="text"
              className="w-full mt-1 px-3 py-1.5 text-sm rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              placeholder="e.g. Direct API integration, no SDK, manual review via vendor portal, batch uploads for monitoring"
              value={bp.integrationMethod}
              onChange={(e) => updateBlueprintField('integrationMethod', e.target.value)}
            />
          </div>

          {/* Discovery questions — collapsible */}
          <DiscoveryQuestions bp={bp} updateBlueprintField={updateBlueprintField} highlightedFields={discoveryHighlights} />

          {/* Cascade Diagrams */}
          {bp.countryRulesets.length > 0 && dealId && (
            <BlueprintCascadeGenerator
              dealId={dealId}
              dealName={scope.customerName || ''}
              countryRulesets={bp.countryRulesets}
            />
          )}

          {linkedCascades.length > 0 && (
            <div className="space-y-4">
              {linkedCascades.map((project) => (
                <div key={project.id}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <GitBranch className="w-3.5 h-3.5 text-blue-400" />
                      <span className="text-xs font-medium text-[var(--text-primary)]">{project.dealName || 'Untitled'}</span>
                      <span className="text-[10px] text-[var(--text-muted)]">
                        {project.cascades.length} {project.cascades.length === 1 ? 'country' : 'countries'} &middot; Updated {new Date(project.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => {
                          const params = new URLSearchParams({ dealId: String(dealId), dealName: scope.customerName || '', from: 'blueprint', projectId: project.id })
                          window.open(`/dashboard/presales/tools/cascade-builder?${params.toString()}`, '_blank')
                        }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 rounded-md transition-colors"
                      >
                        Edit in Builder <ExternalLink className="w-2.5 h-2.5" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this cascade project?')) {
                            useCascadeStore.getState().deleteProject(project.id)
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                        title="Delete cascade project"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {project.cascades.map((cascade) => (
                    <CascadePreview
                      key={cascade.countryCode}
                      nodes={cascade.nodes}
                      edges={cascade.edges}
                      country={cascade.country}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* Section: Verification Stack */}
      <Section
        title="Verification Stack"
        subtitle="Current vendor stack per layer — who does what, who owns it, and where it hurts"
        icon={<Shield className="w-5 h-5" />}
        accentColor="rose"

        complete={(bp.verificationStack || []).some((v) => v.vendor.trim() !== '')}
        headerAction={(bp.verificationStack || []).some((v) => v.vendor.trim()) ? (
          <button
            onClick={() => setEditingStack(!editingStack)}
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md border transition-colors ${
              editingStack
                ? 'text-rose-400 bg-rose-500/15 border-rose-500/30'
                : 'text-[var(--text-muted)] bg-[var(--bg-primary)] border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]'
            }`}
          >
            <Pencil className="w-3 h-3" /> {editingStack ? 'Done' : 'Edit'}
          </button>
        ) : undefined}
      >
        <div className="mt-3">
          {editingStack || !(bp.verificationStack || []).some((v) => v.vendor.trim()) ? (
          <>
          <div className="rounded-lg border border-[var(--border-color)] overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-[200px_160px_1fr] gap-px bg-[var(--border-color)]">
              <div className="px-3 py-2 bg-rose-500/10 text-[10px] font-semibold text-rose-400 uppercase tracking-wide">Layer</div>
              <div className="px-3 py-2 bg-rose-500/10 text-[10px] font-semibold text-rose-400 uppercase tracking-wide">Current Vendor / Approach</div>
              <div className="px-3 py-2 bg-rose-500/10 text-[10px] font-semibold text-rose-400 uppercase tracking-wide">Pain Points / Gaps</div>
            </div>
            {/* Rows */}
            {(bp.verificationStack || []).map((v) => (
              <div key={v.id} className="grid grid-cols-[200px_160px_1fr] gap-px bg-[var(--border-color)]">
                <div className="px-3 py-3 bg-[var(--bg-secondary)] flex items-center gap-1.5">
                  {v.layer === 'other' ? (
                    <div className="flex items-center gap-1 flex-1">
                      <input
                        type="text"
                        placeholder="Custom layer..."
                        value={v.customLabel}
                        onChange={(e) => updateBlueprintVerificationLayer(v.id, { customLabel: e.target.value })}
                        className="flex-1 px-1.5 py-0.5 text-xs font-medium rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                      />
                      <button onClick={() => removeBlueprintVerificationLayer(v.id)} className="p-0.5 text-[var(--text-muted)] hover:text-red-400">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs font-medium text-[var(--text-primary)]">{v.layer}</span>
                  )}
                </div>
                <div className="bg-[var(--bg-primary)]">
                  <input
                    type="text"
                    placeholder="e.g. Onfido, In-house, None"
                    value={v.vendor}
                    onChange={(e) => updateBlueprintVerificationLayer(v.id, { vendor: e.target.value })}
                    className="w-full h-full px-3 py-3 text-xs bg-transparent text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none"
                  />
                </div>
                <div className="bg-[var(--bg-primary)]">
                  <textarea
                    placeholder="Max 5 pain points, one per line. e.g.&#10;High false positive rate&#10;Manual workaround&#10;Single-source dependency"
                    value={v.painPoints}
                    onChange={(e) => updateBlueprintVerificationLayer(v.id, { painPoints: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-3 text-xs bg-transparent text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none resize-y"
                  />
                </div>
              </div>
            ))}
          </div>
          <button onClick={addBlueprintVerificationLayer} className="mt-2 inline-flex items-center gap-1 text-xs text-rose-400 hover:text-rose-300">
            <Plus className="w-3 h-3" /> Add custom layer
          </button>
          </>
          ) : (
          <div className="rounded-lg border border-[var(--border-color)] overflow-hidden">
            <div className="grid grid-cols-[200px_160px_1fr] gap-px bg-[var(--border-color)]">
              <div className="px-3 py-2 bg-rose-500/10 text-[10px] font-semibold text-rose-400 uppercase tracking-wide">Layer</div>
              <div className="px-3 py-2 bg-rose-500/10 text-[10px] font-semibold text-rose-400 uppercase tracking-wide">Current Vendor / Approach</div>
              <div className="px-3 py-2 bg-rose-500/10 text-[10px] font-semibold text-rose-400 uppercase tracking-wide">Pain Points / Gaps</div>
            </div>
            {(bp.verificationStack || []).map((v) => (
              <div key={v.id} className="grid grid-cols-[200px_160px_1fr] gap-px bg-[var(--border-color)]">
                <div className="px-3 py-3 bg-[var(--bg-secondary)]">
                  <span className="text-xs font-medium text-[var(--text-primary)]">{v.layer === 'other' ? (v.customLabel || 'Custom') : v.layer}</span>
                </div>
                <div className="px-3 py-3 bg-[var(--bg-primary)]">
                  <span className="text-xs text-[var(--text-primary)]">{v.vendor || <span className="text-[var(--text-muted)] italic">—</span>}</span>
                </div>
                <div className="px-3 py-3 bg-[var(--bg-primary)]">
                  {v.painPoints ? (() => {
                    const lines = v.painPoints.split(/\n|[•\-]\s+/).map(l => l.trim()).filter(Boolean)
                    const capped = lines.slice(0, 5)
                    return (
                      <ul className="space-y-0.5">
                        {capped.map((l, i) => (
                          <li key={i} className="text-xs text-[var(--text-primary)] flex items-start gap-1">
                            <span className="text-rose-400 mt-0.5 shrink-0">•</span>
                            <span>{l.length > 80 ? l.slice(0, 77) + '…' : l}</span>
                          </li>
                        ))}
                        {lines.length > 5 && <li className="text-[10px] text-[var(--text-muted)] italic ml-3">+{lines.length - 5} more</li>}
                      </ul>
                    )
                  })() : <span className="text-xs text-[var(--text-muted)] italic">—</span>}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </Section>

      {/* Section 4: Country Rulesets */}
      <Section
        title="Country Rulesets & Signal Map"
        subtitle="Verification ruleset, data inputs, fuzziness, national IDs, biometric vendors, and pass rates per country"
        icon={<Globe className="w-5 h-5" />}
        accentColor="emerald"
        complete={bp.countryRulesets.length > 0 && bp.countryRulesets.some((cr) => cr.country && cr.ruleset !== 'unknown' && (cr.dataInputs || []).filter((di) => di.enabled).length > 0 && (cr.dataInputs || []).filter((di) => di.enabled).every((di) => di.confirmed))}

      >
        <div className="mt-3">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] text-[var(--text-muted)]">Map the verification ruleset per country. Toggle data inputs and set fuzziness per field. This reveals single-source dependencies and rule-set gaps.</p>
            <button onClick={addBlueprintCountryRuleset} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-md transition-colors flex-shrink-0">
              <Plus className="w-3.5 h-3.5" /> Add country
            </button>
          </div>
          <InstructionsPanel />
          {bp.countryRulesets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center border-2 border-dashed border-emerald-500/20 rounded-lg bg-emerald-500/5">
              <Globe className="w-8 h-8 text-emerald-500/40 mb-2" />
              <p className="text-sm text-[var(--text-muted)]">No country rulesets documented yet</p>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">Add countries to map their current verification approach</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bp.countryRulesets.map((cr) => {
                const passNum = parseFloat(cr.passRate)
                const passColor = !isNaN(passNum)
                  ? passNum >= 90 ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' : passNum >= 70 ? 'text-amber-400 bg-amber-500/10 border-amber-500/30' : 'text-red-400 bg-red-500/10 border-red-500/30'
                  : 'text-[var(--text-muted)] bg-[var(--bg-secondary)] border-[var(--border-color)]'
                const rulesetColors: Record<string, string> = {
                  '2+2': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 shadow-[0_0_12px_rgba(16,185,129,0.15)]',
                  '1+1': 'bg-amber-500/20 text-amber-400 border-amber-500/40 shadow-[0_0_12px_rgba(245,158,11,0.15)]',
                  'custom': 'bg-purple-500/20 text-purple-400 border-purple-500/40 shadow-[0_0_12px_rgba(168,85,247,0.15)]',
                  'unknown': 'bg-orange-500/20 text-orange-400 border-orange-500/40 shadow-[0_0_12px_rgba(249,115,22,0.15)]',
                }
                const inputs = Array.isArray(cr.dataInputs) && cr.dataInputs.length > 0 ? cr.dataInputs : getDefaultDataInputs()
                const enabledCount = inputs.filter((di) => di.enabled).length

                const countryCompletion = getCountryCompletion(cr)
                const countryName = COUNTRIES.find((c) => c.code === cr.country)?.name

                return (
                  <div key={cr.id} className="rounded-xl border border-emerald-500/20 bg-[var(--bg-secondary)] overflow-hidden">
                    {/* Country header */}
                    <div className="flex items-center gap-4 px-5 py-3 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent border-b border-emerald-500/15">
                      <CountrySearch value={cr.country} onChange={(code) => updateBlueprintCountryRuleset(cr.id, { country: code })} usedCodes={bp.countryRulesets.filter(r => r.id !== cr.id).map(r => r.country).filter(Boolean)} />
                      {/* Per-country completion */}
                      <div className="flex items-center gap-1.5">
                        <div className="w-16 h-2 rounded-full overflow-hidden border border-emerald-500/40 bg-black/30">
                          <div className={`h-full rounded-full transition-all ${countryCompletion === 100 ? 'bg-emerald-400' : countryCompletion > 50 ? 'bg-amber-400' : 'bg-blue-400'}`} style={{ width: `${countryCompletion}%` }} />
                        </div>
                        <span className={`text-[10px] font-bold ${countryCompletion === 100 ? 'text-emerald-300' : countryCompletion > 50 ? 'text-amber-300' : 'text-[var(--text-muted)]'}`}>
                          {countryCompletion}%{countryCompletion === 100 && countryName ? ` — ${countryName} complete` : ''}
                        </span>
                      </div>

                      {/* Ruleset pills */}
                      <div className="flex gap-1.5">
                        {RULESET_OPTIONS.map((o) => (
                          <button
                            key={o.value}
                            onClick={() => updateBlueprintCountryRuleset(cr.id, { ruleset: o.value })}
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 transition-all ${
                              cr.ruleset === o.value ? rulesetColors[o.value] : 'bg-transparent text-[var(--text-muted)] border-[var(--border-color)] hover:bg-[var(--bg-primary)]'
                            }`}
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>

                      {/* Gov ID toggle */}
                      <button
                        onClick={() => updateBlueprintCountryRuleset(cr.id, { govIdRequired: !cr.govIdRequired })}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border-2 transition-all ${
                          cr.govIdRequired
                            ? 'bg-violet-500/20 text-violet-400 border-violet-500/40 shadow-[0_0_12px_rgba(139,92,246,0.15)]'
                            : 'bg-transparent text-[var(--text-muted)] border-[var(--border-color)] hover:bg-[var(--bg-primary)]'
                        }`}
                      >
                        Gov ID {cr.govIdRequired ? 'Required' : 'Optional'}
                      </button>

                      <div className="ml-auto flex items-center gap-3">
                        {/* Current pass rate */}
                        <div className={`flex items-center gap-1 px-3 py-1 rounded-lg border ${passColor}`}>
                          <button
                            onClick={() => updateBlueprintCountryRuleset(cr.id, { passRateConfirmed: !cr.passRateConfirmed })}
                            className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 border transition-all ${
                              cr.passRateConfirmed ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-amber-500 border-amber-400 text-white hover:brightness-110'
                            }`}
                            title={cr.passRateConfirmed ? 'Confirmed' : 'Unconfirmed'}
                          >{cr.passRateConfirmed ? '✓' : '?'}</button>
                          <span className="text-[10px] uppercase tracking-wide opacity-70">Current</span>
                          <input
                            type="text"
                            placeholder="—"
                            value={cr.passRate}
                            onChange={(e) => updateBlueprintCountryRuleset(cr.id, { passRate: e.target.value })}
                            className="w-10 text-sm font-bold text-center bg-transparent outline-none placeholder:opacity-40"
                          />
                          <span className="text-xs font-bold opacity-70">%</span>
                        </div>
                        {/* Desired pass rate */}
                        {(() => {
                          const desiredNum = parseFloat(cr.desiredPassRate || '')
                          const desiredColor = !isNaN(desiredNum)
                            ? desiredNum >= 90 ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' : desiredNum >= 70 ? 'text-amber-400 bg-amber-500/10 border-amber-500/30' : 'text-red-400 bg-red-500/10 border-red-500/30'
                            : 'text-[var(--text-muted)] bg-[var(--bg-secondary)] border-[var(--border-color)]'
                          return (
                            <div className={`flex items-center gap-1 px-3 py-1 rounded-lg border ${desiredColor}`}>
                              <button
                                onClick={() => updateBlueprintCountryRuleset(cr.id, { desiredPassRateConfirmed: !cr.desiredPassRateConfirmed })}
                                className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 border transition-all ${
                                  cr.desiredPassRateConfirmed ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-amber-500 border-amber-400 text-white hover:brightness-110'
                                }`}
                                title={cr.desiredPassRateConfirmed ? 'Confirmed' : 'Unconfirmed'}
                              >{cr.desiredPassRateConfirmed ? '✓' : '?'}</button>
                              <span className="text-[10px] uppercase tracking-wide opacity-70">Target</span>
                              <input
                                type="text"
                                placeholder="—"
                                value={cr.desiredPassRate || ''}
                                onChange={(e) => updateBlueprintCountryRuleset(cr.id, { desiredPassRate: e.target.value })}
                                className="w-10 text-sm font-bold text-center bg-transparent outline-none placeholder:opacity-40"
                              />
                              <span className="text-xs font-bold opacity-70">%</span>
                            </div>
                          )
                        })()}
                        <button onClick={() => removeBlueprintCountryRuleset(cr.id)} className="p-1.5 text-[var(--text-muted)] hover:text-red-400 rounded-md hover:bg-red-500/10 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Data inputs — compact pills with status indicator */}
                    <div className="px-4 py-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[9px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">Data inputs</span>
                        <span className="text-[9px] text-[var(--text-muted)]">
                          {enabledCount}/{inputs.length} selected
                          {enabledCount > 0 && (() => {
                            const confirmed = inputs.filter((di) => di.enabled && di.confirmed).length
                            return <span className={confirmed === enabledCount ? ' text-emerald-400' : ' text-amber-400'}>{` · ${confirmed} confirmed`}</span>
                          })()}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {inputs.map((di) => {
                          const pillColors: Record<string, string> = {
                            'First name': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40', 'Middle name': 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
                            'Last name': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40', 'DOB': 'bg-amber-500/20 text-amber-300 border-amber-500/40',
                            'Address': 'bg-violet-500/20 text-violet-300 border-violet-500/40', 'Email': 'bg-blue-500/20 text-blue-300 border-blue-500/40',
                            'Phone': 'bg-blue-500/15 text-blue-300 border-blue-500/30', 'National ID / SSN': 'bg-rose-500/20 text-rose-300 border-rose-500/40',
                            'Document scan': 'bg-orange-500/20 text-orange-300 border-orange-500/40', 'Selfie': 'bg-pink-500/20 text-pink-300 border-pink-500/40',
                            'Device data': 'bg-teal-500/20 text-teal-300 border-teal-500/40',
                          }
                          const activeColor = pillColors[di.type] || 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                          if (!di.enabled) {
                            return (
                              <div key={di.type} className="inline-flex items-center gap-0.5 pl-1 pr-2 py-0.5 rounded-full border bg-[var(--bg-primary)] border-[var(--border-color)] opacity-40 hover:opacity-70 transition-all">
                                <button
                                  onClick={(e) => { e.stopPropagation(); updateBlueprintCountryInputField(cr.id, di.type, { confirmed: !di.confirmed }) }}
                                  className={`w-3 h-3 rounded-full flex items-center justify-center text-[7px] font-bold flex-shrink-0 border transition-all ${
                                    di.confirmed ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-amber-500 border-amber-400 text-white'
                                  }`}
                                  title={di.confirmed ? 'Confirmed not needed' : 'Unknown — click to confirm not needed'}
                                >{di.confirmed ? '✓' : '?'}</button>
                                <button onClick={() => toggleBlueprintCountryInputData(cr.id, di.type)}
                                  className="text-[11px] font-medium text-[var(--text-muted)]">
                                  {di.type}
                                </button>
                              </div>
                            )
                          }
                          return (
                            <div key={di.type} className={`relative inline-flex items-center gap-1 pl-1.5 pr-2 py-0.5 rounded-full border transition-all ${activeColor}`}>
                              {/* Status indicator */}
                              <button
                                onClick={() => updateBlueprintCountryInputField(cr.id, di.type, { confirmed: !di.confirmed })}
                                className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 border transition-all ${
                                  di.confirmed ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-amber-500 border-amber-400 text-white hover:brightness-110'
                                }`}
                                title={di.confirmed ? 'Confirmed' : 'Unconfirmed — click to confirm'}
                              >{di.confirmed ? '✓' : '?'}</button>
                              <button onClick={() => toggleBlueprintCountryInputData(cr.id, di.type)} className="text-[11px] font-semibold hover:line-through">{di.type}</button>
                              <select value={di.fuzziness} onChange={(e) => updateBlueprintCountryInputFuzziness(cr.id, di.type, e.target.value as BlueprintFuzziness)}
                                className="text-[9px] font-bold bg-white/10 border-0 rounded-full px-1 py-0 outline-none cursor-pointer appearance-none">
                                <option value="unknown">?</option><option value="strict">S</option><option value="moderate">M</option><option value="fuzzy">F</option><option value="optional">Opt</option>
                              </select>
                            </div>
                          )
                        })}
                      </div>

                      {/* Compact field detail rows */}
                      {enabledCount > 0 && (
                        <div className="mt-2 space-y-px">
                          {inputs.filter((di) => di.enabled).map((di) => {
                            const isBasicField = ['First name', 'Middle name', 'Last name', 'DOB', 'Address', 'Email', 'Phone'].includes(di.type)
                            const isAddress = di.type === 'Address'
                            const isNationalId = di.type === 'National ID / SSN'
                            const isDocScan = di.type === 'Document scan'
                            const isSelfie = di.type === 'Selfie'
                            const isDevice = di.type === 'Device data'
                            const suggestedIds = isNationalId && cr.country ? getIdTypesForCountry(cr.country) : null
                            const incomplete = !di.confirmed
                            return (
                              <div key={di.type} className={`flex items-start gap-1.5 py-1 px-1.5 rounded text-[10px] ${incomplete ? 'bg-amber-500/5' : ''}`}>
                                <span className={`w-20 flex-shrink-0 font-semibold truncate pt-px ${incomplete ? 'text-amber-400/80' : 'text-[var(--text-secondary)]'}`}>{di.type}</span>
                                <div className="flex-1 flex flex-wrap items-center gap-1 min-w-0">
                                  {/* Capture method */}
                                  {isBasicField ? (
                                    <select value={di.captureMethod || ''} onChange={(e) => updateBlueprintCountryInputCaptureMethod(cr.id, di.type, e.target.value)}
                                      className="px-1.5 py-0 text-[10px] rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] cursor-pointer">
                                      <option value="">Capture method...</option>
                                      {CAPTURE_METHODS.map((cm) => <option key={cm.value} value={cm.value}>{cm.label}</option>)}
                                    </select>
                                  ) : (
                                    <select value={di.captureMethod || ''} onChange={(e) => updateBlueprintCountryInputCaptureMethod(cr.id, di.type, e.target.value)}
                                      className="px-1.5 py-0 text-[10px] rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] cursor-pointer">
                                      <option value="">Capture method...</option>
                                      {CAPTURE_METHODS.map((cm) => <option key={cm.value} value={cm.value}>{cm.label}</option>)}
                                    </select>
                                  )}
                                  {/* Address format */}
                                  {isAddress && (
                                    <select value={di.addressFormat || ''} onChange={(e) => updateBlueprintCountryInputField(cr.id, di.type, { addressFormat: e.target.value })}
                                      className="px-1.5 py-0 text-[10px] rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] cursor-pointer">
                                      <option value="">Format...</option>
                                      {ADDRESS_FORMATS.map((af) => <option key={af.value} value={af.value}>{af.label}</option>)}
                                    </select>
                                  )}
                                  {/* National ID — multi-select dropdown */}
                                  {isNationalId && suggestedIds && (() => {
                                    const selected = cr.nationalIds || []
                                    const label = selected.length === 0 ? 'Select ID types...'
                                      : selected.length === 1 ? (suggestedIds.find((t) => t.id === selected[0])?.label || selected[0])
                                      : `Multiple (${selected.length})`
                                    return (
                                      <select value="" onChange={(e) => {
                                        if (!e.target.value) return
                                        const val = e.target.value
                                        const cur = cr.nationalIds || []
                                        updateBlueprintCountryRuleset(cr.id, { nationalIds: cur.includes(val) ? cur.filter((id) => id !== val) : [...cur, val] })
                                        e.target.value = ''
                                      }} className="px-1.5 py-0 text-[10px] rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] cursor-pointer min-w-[8rem]">
                                        <option value="">{label}</option>
                                        {suggestedIds.map((idType) => (
                                          <option key={idType.id} value={idType.id}>{selected.includes(idType.id) ? '✓ ' : '  '}{idType.label}</option>
                                        ))}
                                        <option value="_other">Other (add in notes)</option>
                                      </select>
                                    )
                                  })()}
                                  {/* Vendor multi-select dropdowns */}
                                  {isSelfie && <MultiSelectDropdown options={SELFIE_VENDORS} selected={cr.selfieVendors || []} onChange={(v) => updateBlueprintCountryRuleset(cr.id, { selfieVendors: v })} placeholder="Selfie vendor..." />}
                                  {isDocScan && <MultiSelectDropdown options={DOC_SCAN_VENDORS} selected={cr.docScanVendors || []} onChange={(v) => updateBlueprintCountryRuleset(cr.id, { docScanVendors: v })} placeholder="Doc scan vendor..." />}
                                  {isDevice && <MultiSelectDropdown options={DEVICE_DATA_VENDORS} selected={cr.deviceDataVendors || []} onChange={(v) => updateBlueprintCountryRuleset(cr.id, { deviceDataVendors: v })} placeholder="Device vendor..." />}
                                  {/* Notes */}
                                  <input type="text" placeholder="Notes..." value={di.notes || ''}
                                    onChange={(e) => updateBlueprintCountryInputField(cr.id, di.type, { notes: e.target.value })}
                                    className="flex-1 min-w-[4rem] px-1.5 py-0 text-[10px] rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]" />
                                  {/* Row confirmed indicator */}
                                  <button
                                    onClick={() => updateBlueprintCountryInputField(cr.id, di.type, { confirmed: !di.confirmed })}
                                    className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0 border transition-all ${
                                      di.confirmed ? 'bg-emerald-500 border-emerald-400 text-white' : 'bg-amber-500 border-amber-400 text-white hover:brightness-110'
                                    }`}
                                    title={di.confirmed ? 'Confirmed' : 'Unconfirmed — click to confirm'}
                                  >{di.confirmed ? '✓' : '?'}</button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {/* Notes */}
                      <textarea
                        placeholder="Notes — single-source dependency, cascade details, known issues..."
                        value={cr.notes}
                        onChange={(e) => updateBlueprintCountryRuleset(cr.id, { notes: e.target.value })}
                        className="w-full mt-4 px-3 py-1.5 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y"
                        rows={cr.notes && cr.notes.length > 80 ? 3 : 1}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Section>

      {/* Section 5: Technical Stakeholder Map */}
      <Section
        title="Technical Stakeholder Map"
        subtitle="Who are we engaging technically? What do they care about? Are they evaluators or champions?"
        icon={<Users className="w-5 h-5" />}
        defaultOpen={false}
        complete={bp.stakeholders.length > 0 && bp.stakeholders.some((s) => s.name.trim() !== '')}
        accentColor="amber"

        headerAction={bp.stakeholders.length > 0 && bp.stakeholders.some((s) => s.name.trim()) ? (
          <button
            onClick={() => setEditingStakeholders(!editingStakeholders)}
            className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md border transition-colors ${
              editingStakeholders
                ? 'text-amber-400 bg-amber-500/15 border-amber-500/30'
                : 'text-[var(--text-muted)] bg-[var(--bg-primary)] border-[var(--border-color)] hover:bg-[var(--bg-tertiary)]'
            }`}
          >
            <Pencil className="w-3 h-3" /> {editingStakeholders ? 'Done' : 'Edit'}
          </button>
        ) : undefined}
      >
        <div className="mt-3">
          {editingStakeholders || bp.stakeholders.length === 0 || !bp.stakeholders.some((s) => s.name.trim()) ? (
          <>
          <div className="flex justify-end mb-2">
            <button onClick={addBlueprintStakeholder} className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
              <Plus className="w-3 h-3" /> Add stakeholder
            </button>
          </div>
          {bp.stakeholders.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] italic py-2">No stakeholders mapped yet.</p>
          ) : (
            <div className="space-y-2">
              {bp.stakeholders.map((s) => {
                return (
                <div key={s.id} className="p-3 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Name"
                      value={s.name}
                      onChange={(e) => updateBlueprintStakeholder(s.id, { name: e.target.value })}
                      className="w-40 px-2 py-1.5 text-xs font-semibold rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                    />
                    <input
                      type="text"
                      placeholder="Role / title"
                      value={s.role}
                      onChange={(e) => updateBlueprintStakeholder(s.id, { role: e.target.value })}
                      className="flex-1 px-2 py-1.5 text-xs rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                    />
                    <div className="flex gap-1">
                      {ENGAGEMENT_STATES.map((es) => {
                        const active = s.engagementState === es.value
                        const btnColor =
                          es.value === 'champion' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                          : es.value === 'champion_candidate' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                          : es.value === 'blocker' ? 'bg-red-500/15 text-red-400 border-red-500/30'
                          : es.value === 'evaluator' ? 'bg-blue-500/15 text-blue-400 border-blue-500/30'
                          : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] border-[var(--border-color)]'
                        return (
                          <button
                            key={es.value}
                            onClick={() => updateBlueprintStakeholder(s.id, { engagementState: es.value })}
                            className={`px-2 py-1 text-[10px] font-medium rounded border transition-colors ${
                              active ? btnColor : 'bg-transparent text-[var(--text-muted)] border-[var(--border-color)] hover:bg-[var(--bg-primary)]'
                            }`}
                          >
                            {es.label}
                          </button>
                        )
                      })}
                    </div>
                    <button onClick={() => removeBlueprintStakeholder(s.id)} className="p-1 text-[var(--text-muted)] hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <textarea
                    placeholder="Technical concerns — what do they care about? What questions have they asked? What are they testing us on?"
                    value={s.technicalConcerns}
                    onChange={(e) => updateBlueprintStakeholder(s.id, { technicalConcerns: e.target.value })}
                    className="w-full px-2 py-1.5 text-xs rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-y"
                    rows={2}
                  />
                </div>
                )
              })}
            </div>
          )}
          </>
          ) : (
            <div className="space-y-2">
              {bp.stakeholders.map((s) => {
                const engagementLabel = ENGAGEMENT_STATES.find((es) => es.value === s.engagementState)?.label || 'Unknown'
                const engagementBadgeColor =
                  s.engagementState === 'champion' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                  : s.engagementState === 'champion_candidate' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                  : s.engagementState === 'blocker' ? 'bg-red-500/15 text-red-400 border-red-500/30'
                  : s.engagementState === 'evaluator' ? 'bg-blue-500/15 text-blue-400 border-blue-500/30'
                  : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] border-[var(--border-color)]'
                return (
                  <div key={s.id} className="p-3 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-[var(--text-primary)]">{s.name}</span>
                      {s.role && <span className="text-[11px] text-[var(--text-secondary)]">— {s.role}</span>}
                      <span className={`px-1.5 py-0.5 text-[9px] font-medium rounded border ${engagementBadgeColor}`}>{engagementLabel}</span>
                    </div>
                    {s.technicalConcerns && (
                      <p className="text-xs text-[var(--text-secondary)] whitespace-pre-wrap">{s.technicalConcerns}</p>
                    )}
                  </div>
                )
              })}
              <button onClick={() => { addBlueprintStakeholder(); setEditingStakeholders(true) }} className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300">
                <Plus className="w-3 h-3" /> Add stakeholder
              </button>
            </div>
          )}
        </div>
      </Section>


      {/* Preparation Checklist */}
      <Section
        title="Preparation Checklist"
        subtitle="Before every call on a Tier-1/Tier-2 deal — nobody walks in cold"
        icon={<CheckSquare className="w-5 h-5" />}
        accentColor="emerald"

        complete={bp.prepChecklist.every((c) => c.checked)}
        defaultOpen={false}
      >
        <div className="mt-3">
          <p className="text-[11px] text-[var(--text-muted)] mb-3">
            Preparation for an enterprise discovery call is the most important hour any seller spends in a week. It is not optional.
            Reset the checklist before each call to ensure every item is re-verified.
          </p>
          <div className="space-y-1.5">
            {bp.prepChecklist.map((item) => (
              <button
                key={item.id}
                onClick={() => toggleBlueprintPrepCheck(item.id)}
                className={`flex items-start gap-3 py-2.5 px-3 rounded-lg cursor-pointer transition-all border text-left w-full ${
                  item.checked
                    ? 'bg-emerald-500/10 border-emerald-500/20'
                    : 'bg-[var(--bg-secondary)] border-[var(--border-color)] hover:border-emerald-500/30'
                }`}
              >
                <div className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${
                  item.checked
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'border-[var(--border-color)] bg-[var(--bg-primary)]'
                }`}>
                  {item.checked && <CheckSquare className="w-3.5 h-3.5" />}
                </div>
                <span className={`text-sm ${item.checked ? 'text-emerald-400/80 line-through' : 'text-[var(--text-primary)]'}`}>
                  {item.label}
                </span>
              </button>
            ))}
          </div>
          <button
            onClick={resetBlueprintPrepChecklist}
            className="mt-3 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] underline"
          >
            Reset checklist (before next call)
          </button>
        </div>
      </Section>
    </div>
  )
}
